// frontend/src/components/chat/ChatInterface.js
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { sanitizeInlineHTML, sanitizeRichHTML } from '../../utils/sanitize';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
  Chip,
  Collapse,
  Card,
  CardContent,
  CardActions,
  Button,
  Divider,
  Alert,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Menu,
  Drawer,
  AppBar,
  Toolbar,
  Tooltip,
  Switch,
  FormControlLabel,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CodeIcon from '@mui/icons-material/Code';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ChatIcon from '@mui/icons-material/Chat';
import DashboardIcon from '@mui/icons-material/Dashboard';

import MenuIcon from '@mui/icons-material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TuneIcon from '@mui/icons-material/Tune';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import {
  MessagePerformanceIndicator,
  ResearchProgressBar
} from './TemplateComponents';
import TemplateParameterControls from './TemplateParameterControls';
import DynamicTemplateResults from './DynamicTemplateResults';
import TokenUsageDisplay from './TokenUsageDisplay';
import RecipeQuickActions from './RecipeQuickActions';
import ChatDocumentUpload from './ChatDocumentUpload';
import ConversationDebugLogs from './ConversationDebugLogs';
import ConversationContextIndicator from './ConversationContextIndicator';
import BugReportIcon from '@mui/icons-material/BugReport';
import TemplateBrowser from './TemplateBrowser';
import PinnedTemplates from './PinnedTemplates';
import { MessageRenderer } from './MessageComponents';

// Ensure axios sends credentials (cookies) with requests by default
axios.defaults.withCredentials = true;
// No token is pinned on axios defaults here (DT-1521): App.js imports this module on every page load, so a default set
// from the page-load token would outlive an in-app logout and the next user's sign-in (the backend reads x-auth-token
// first). It was only overridden per request by the interceptor in hooks/useAnalytics.js. App.js's request interceptor
// now sets both auth headers from the current token on every request.

const DRAWER_WIDTH = 300;

// Utility function for formatting numbers in recipe query result tables
const formatTableValue = (value, columnName) => {
  if (value === null || value === undefined || value === '') return '-';

  // If it's not a number, return as string
  if (typeof value !== 'number') return String(value);

  const lowerCol = (columnName || '').toLowerCase();

  // Currency columns (spend, revenue, cpa, cac, aov)
  if (lowerCol.includes('spend') || lowerCol.includes('revenue') ||
      lowerCol.includes('cpa') || lowerCol.includes('cac') ||
      lowerCol.includes('aov') || lowerCol.includes('cost') ||
      lowerCol.includes('value') || lowerCol.includes('price')) {
    return '$' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // Percentage columns (roas, ncroas, rate, ctr, cvr)
  if (lowerCol.includes('roas') || lowerCol.includes('ncroas') ||
      lowerCol.includes('_rate') || lowerCol.includes('ctr') ||
      lowerCol.includes('cvr') || lowerCol.includes('conversion')) {
    // If value is already a ratio (like 2.5 for 2.5x ROAS), format to 2 decimals
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // Count columns (orders, sessions, visitors, clicks, impressions)
  if (lowerCol.includes('order') || lowerCol.includes('session') ||
      lowerCol.includes('visitor') || lowerCol.includes('click') ||
      lowerCol.includes('impression') || lowerCol.includes('count') ||
      lowerCol.includes('_total')) {
    return Math.round(value).toLocaleString('en-US');
  }

  // Default: format with up to 2 decimal places, no trailing zeros
  if (Number.isInteger(value)) {
    return value.toLocaleString('en-US');
  }
  return value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

// The template routes refuse a conversation id this user cannot access, as /message does (DT-1249, DT-1277), e.g.
// one left in localStorage by another login. The caller drops the stored id so the next run starts a new one.
const isConversationDenied = (error) =>
  Boolean(error?.conversationDenied || error?.response?.data?.code === 'CONVERSATION_ACCESS_DENIED');
// One wording for every path (the send path's own text starts the same way), plus what to do next on that path
const CONVERSATION_DENIED_TEXT = 'That conversation is not available to you, so a new one will start.';
const TEMPLATE_CONVERSATION_DENIED_TEXT = `${CONVERSATION_DENIED_TEXT} Run the report again.`;
const DOCUMENTS_CONVERSATION_DENIED_TEXT = `${CONVERSATION_DENIED_TEXT} The old conversation's documents do not carry over: re-attach any you need.`;
const UPLOAD_CONVERSATION_DENIED_TEXT = `${CONVERSATION_DENIED_TEXT} Upload the file again.`;

// Drop a refused conversation id and say so as a warning, but only while it is still the current one: a late refusal
// after the user moved to another conversation changes nothing. The typed input is left alone (DT-1514): these paths
// never clear the box, unlike the send path, which puts the message it sent back itself (DT-1501).
// localStorage mirrors the current id (effect below + every reset).
// cachedMessages (DT-1531): the message list the page opened with, when deniedId is the conversation it opened with.
// Those messages are this browser's cached copy of the refused conversation (possibly another login's): while the
// chat still shows exactly them, they are replaced by the warning. Whichever page-load request is refused first
// (documents or history) does this; the second finds the chat already replaced and adds nothing. The replacement does
// not depend on localStorage: another tab may have moved currentConversationId meanwhile, and this tab must still stop
// showing the refused conversation's cached messages (DT-1539).
const dropDeniedConversation = (setConversationId, setMessages, deniedId, text, source, cachedMessages) => {
  console.warn('[Chat] CONVERSATION_ACCESS_DENIED', { conversationId: deniedId, source });
  setConversationId(current => (current === deniedId ? null : current));
  const stillStored = localStorage.getItem('currentConversationId') === deniedId;
  if (stillStored) localStorage.removeItem('currentConversationId');
  const warning = {
    id: Date.now() + Math.random(), sender: 'assistant', text, timestamp: new Date(), type: 'error', severity: 'warning'
  };
  setMessages(prev => {
    if (cachedMessages && prev === cachedMessages) return [warning];
    return stillStored ? [...prev, warning] : prev;
  });
};

const ChatInterface = () => {
  // State management with persistence
  const [messages, setMessages] = useState(() => {
    // Restore messages from localStorage on initial load
    const savedMessages = localStorage.getItem('chatMessages');
    if (savedMessages) {
      try {
        const parsedMessages = JSON.parse(savedMessages);
        // Convert timestamp strings back to Date objects and handle invalid dates
        return parsedMessages.map(msg => ({
          ...msg,
          timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date()
        })).filter(msg => msg.timestamp instanceof Date && !isNaN(msg.timestamp));
      } catch (error) {
        console.warn('Failed to parse saved messages:', error);
      }
    }
    // Default welcome message
    return [
      {
        id: 1,
        sender: 'assistant',
        text: 'Hi! I\'m your enhanced AI data analyst with multi-step research capabilities. Please select a brand from the dropdown above, and I can conduct comprehensive analysis of your marketing data. What would you like to explore?',
        timestamp: new Date(),
        type: 'text'
      }
    ];
  });

  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [restoringHistory, setRestoringHistory] = useState(false); // page-load restore of an empty chat (DT-1517)
  const [researchProgress, setResearchProgress] = useState(null);
  const [conversationId, setConversationId] = useState(() => {
    // Try to restore conversation ID from localStorage
    const stored = localStorage.getItem('currentConversationId');
    console.log('[INIT] 📍 Initial conversationId from localStorage:', stored);
    // Stored conversation ids are UUIDs. Older builds saved a made-up Date.now() id after a Quick Report, which
    // /message now refuses (DT-1249): start without it instead of failing the first message.
    return stored && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(stored) ? stored : null;
  });
  // The conversation this page opened with: the only one restored from the server on load (DT-1517)
  const pageLoadConversationIdRef = useRef(conversationId);
  // ...and what the chat showed when it opened, i.e. this browser's cached copy of it (DT-1531)
  const pageLoadRef = useRef({ conversationId, messages });

  // Feature flag for experts
  const FEATURE_EXPERTS = (process.env.REACT_APP_FEATURE_EXPERTS ?? 'true') === 'true';

  // Brand management state with persistence
  const [availableBrands, setAvailableBrands] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState(() => {
    // Restore selected brand from localStorage
    return localStorage.getItem('selectedBrandId') || '';
  });
  // The brand selected now, for answers that arrive after an await (a brand switch may have happened meanwhile)
  const selectedBrandIdRef = useRef(selectedBrandId);
  selectedBrandIdRef.current = selectedBrandId;
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [brandSchema, setBrandSchema] = useState(null);

  // Experts state (Phase 1)
  const [experts, setExperts] = useState([]);
  const [expertsLoading, setExpertsLoading] = useState(false);
  const [selectedExpertId, setSelectedExpertId] = useState(() => localStorage.getItem('selectedExpertId') || '');

  // Conversation management state
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [contextMenuAnchor, setContextMenuAnchor] = useState(null);
  const [selectedConversationForMenu, setSelectedConversationForMenu] = useState(null);

  // Conversation documents state
  const [conversationDocuments, setConversationDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  // Template system state
  const [templateStats, setTemplateStats] = useState(null);
  const [showQuickActions, setShowQuickActions] = useState(true);

  // MCP status (MCP-only mode, Enhanced Chat disabled)
  const [mcpStatus, setMcpStatus] = useState(null);
  const [loadingMcpStatus, setLoadingMcpStatus] = useState(false);

  // GenAI Provider state with persistence
  const [aiProvider, setAiProvider] = useState(() => {
    // Restore AI provider preference from localStorage
    const savedProvider = localStorage.getItem('aiProvider');
    // Normalize old values from previous dropdown
    if (savedProvider === 'claude') return 'claude-sonnet';
    if (savedProvider === 'gemini') return 'gemini-pro';
    return savedProvider || 'auto'; // Default to AUTO mode
  });

  // Track last tools used by MCP for display in header
  const [lastToolsUsed, setLastToolsUsed] = useState([]);

  // Dynamic template state
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [templateParameters, setTemplateParameters] = useState({});

  // Tab state for Browse / Favourites
  const [activeTab, setActiveTab] = useState(0); // 0 = Browse All, 1 = Favourites

  // Hybrid UI state: slim bar with expandable panel
  const [templatePanelExpanded, setTemplatePanelExpanded] = useState(false);
  const [quickAccessTemplates, setQuickAccessTemplates] = useState([]); // Quick report templates for quick bar
  const [pinnedTemplateIds, setPinnedTemplateIds] = useState(new Set()); // Track which templates are pinned/favourited

  // Resizable tab container state - using CSS resize
  const [tabHeight, setTabHeight] = useState(() => {
    const saved = localStorage.getItem('tabContainerHeight');
    return saved ? parseInt(saved) : 200; // Default 200px
  });
  const tabContainerRef = useRef(null);

  // Debug logs state
  const [showDebugLogs, setShowDebugLogs] = useState(false);
  const [lastMessageId, setLastMessageId] = useState(null);

  // 🔥 PHASE 5: Conversation context state
  const [conversationContext, setConversationContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);

  // Flag to prevent auto-loading history when creating a new conversation
  const skipHistoryLoadRef = useRef(false);
  // Bumped whenever the chat on screen is replaced (new conversation, brand/expert switch, a conversation opened from
  // the list). A run that started in an earlier chat checks it before writing its result (DT-1523).
  const chatGenerationRef = useRef(0);
  // The conversation on screen now, for callbacks that finish later than the render they were made in (DT-1562)
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;
  // The generation the running request (typed message, Quick Report, recipe, Browse Template) started in, while it runs
  // (null otherwise). A reset ends that run's busy state, so the new chat is not left with a spinner and a locked
  // input until the abandoned request answers.
  const runGenerationRef = useRef(null);
  const startNewChatGeneration = () => {
    if (runGenerationRef.current === chatGenerationRef.current) {
      runGenerationRef.current = null;
      setLoading(false);
      setResearchProgress(null);
    }
    chatGenerationRef.current += 1;
    setLastMessageId(null); // the debug-logs panel must not open on the previous chat's message
  };
  // A request's answer belongs to the chat it started in. If the user starts a new conversation or switches brand,
  // expert or conversation meanwhile, its writes are dropped instead of landing in (or replacing) the new chat, and it
  // does not adopt its conversation id back (DT-1523, DT-1536). Its busy state was already ended by the reset and may
  // now belong to a newer request, so end() only ends it while the chat is still the run's.
  const beginChatRun = useCallback(() => {
    const generation = chatGenerationRef.current;
    runGenerationRef.current = generation;
    const stillThisChat = () => chatGenerationRef.current === generation;
    return {
      stillThisChat,
      addMessage: (message) => { if (stillThisChat()) setMessages(prev => [...prev, message]); },
      progress: (value) => { if (stillThisChat()) setResearchProgress(value); },
      end: () => {
        if (!stillThisChat()) return;
        runGenerationRef.current = null;
        setLoading(false);
      },
    };
  }, []);
  // Bumped after a refused send (DT-1501) to put the cursor back in the box
  const [refocusInput, setRefocusInput] = useState(0);
  const messageInputRef = useRef(null);
  const loadConversationCallRef = useRef(0);       // bumped per conversation opened from the list; the latest one wins
  const creatingConversationRef = useRef(null);   // { brandId, generation, promise } while ensureConversationId creates one (DT-1294)

  const messagesEndRef = useRef(null);

  // Scroll to bottom whenever messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load available brands on component mount
  useEffect(() => {
    loadAvailableBrands();
  }, []);

  // Load brand schema and conversations when brand is selected
  useEffect(() => {
    if (selectedBrandId) {
      loadBrandSchema(selectedBrandId);
      loadConversations(selectedBrandId);
    } else {
      setBrandSchema(null);
      setConversations([]);
    }
  }, [selectedBrandId]);

  // Load experts when brand changes (Phase 1)
  useEffect(() => {
    if (FEATURE_EXPERTS && selectedBrandId) {
      loadExperts(selectedBrandId);
    } else {
      setExperts([]);
      setSelectedExpertId('');
    }
  }, [FEATURE_EXPERTS, selectedBrandId]);

  // Load template stats
  useEffect(() => {
    loadTemplateStats();
  }, []);

  // Check MCP status when component mounts or toggle changes
  useEffect(() => {
    checkMcpStatus();
  }, []);

  // Save tab height to localStorage when resize ends (CSS-based resize)
  useEffect(() => {
    const container = tabContainerRef.current;
    if (!container) return;

    let saveTimeout = null;

    const handleResizeEnd = () => {
      // Debounce saves to avoid too many localStorage writes
      if (saveTimeout) {
        clearTimeout(saveTimeout);
      }
      saveTimeout = setTimeout(() => {
        const currentHeight = container.offsetHeight;
        if (currentHeight > 0) {
          localStorage.setItem('tabContainerHeight', currentHeight.toString());
          setTabHeight(currentHeight);
        }
      }, 150); // Save 150ms after resize ends
    };

    // Listen for mouseup to detect when user finishes resizing
    const handleMouseUp = () => {
      handleResizeEnd();
    };

    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      if (saveTimeout) {
        clearTimeout(saveTimeout);
      }
    };
  }, []);

  // **PERSISTENCE EFFECTS** - Save state to localStorage when it changes

  // Save messages to localStorage whenever they change
  useEffect(() => {
    if (!localStorage.getItem('token')) return; // signed out: keep nothing for the next person here (DT-1521)
    try {
      localStorage.setItem('chatMessages', JSON.stringify(messages));
    } catch (error) {
      console.warn('Failed to save messages to localStorage:', error);
    }
  }, [messages]);

  // Save conversation ID when it changes
  useEffect(() => {
    if (!localStorage.getItem('token')) return; // signed out: keep nothing for the next person here (DT-1521)
    if (conversationId) {
      localStorage.setItem('currentConversationId', conversationId);
    }
  }, [conversationId]);

  // Save selected brand when it changes
  useEffect(() => {
    if (!localStorage.getItem('token')) return; // signed out: keep nothing for the next person here (DT-1521)
    if (selectedBrandId) {
      localStorage.setItem('selectedBrandId', selectedBrandId);
    }
  }, [selectedBrandId]);

  // Save selected expert when it changes
  useEffect(() => {
    if (!localStorage.getItem('token')) return; // signed out: keep nothing for the next person here (DT-1521)
    if (selectedExpertId) {
      localStorage.setItem('selectedExpertId', selectedExpertId);
    } else {
      localStorage.removeItem('selectedExpertId');
    }
  }, [selectedExpertId]);

  // Save AI provider preference when it changes
  useEffect(() => {
    localStorage.setItem('aiProvider', aiProvider);
  }, [aiProvider]);

  // 📍 Debug: Monitor conversationId changes
  useEffect(() => {
    console.log('[DEBUG] 📍 conversationId changed to:', conversationId);
    console.log('[DEBUG] 📍 conversationId type:', typeof conversationId);
    console.log('[DEBUG] 📍 conversationId truthy?:', !!conversationId);
    console.log('[DEBUG] 📍 Debug icon should be:', conversationId ? 'ENABLED' : 'DISABLED');
  }, [conversationId]);

  // 📍 Debug: Monitor lastMessageId changes
  useEffect(() => {
    console.log('[DEBUG] 📍 lastMessageId changed to:', lastMessageId);
  }, [lastMessageId]);

  // **RESTORATION EFFECT** - Load conversation history when brand/conversation changes
  useEffect(() => {
    if (selectedBrandId && conversationId) {
      // Skip loading history if we just created a new conversation
      if (skipHistoryLoadRef.current) {
        console.log('[SKIP] Skipping history load for new conversation');
        skipHistoryLoadRef.current = false;
        return;
      }
      // Only the conversation the page opened with (DT-1517). Every later id comes from this tab, which already shows
      // its messages: the sidebar's loadConversation sets them, and a send or recipe run that adopts a new id is still
      // rendering its reply, which a history reload would race.
      if (conversationId !== pageLoadConversationIdRef.current) return;
      pageLoadConversationIdRef.current = null;
      console.log('[LOAD] Loading conversation history for:', conversationId);
      loadConversationHistory(conversationId);
    }
  }, [selectedBrandId, conversationId]);

  // Load conversation documents when conversation changes
  useEffect(() => {
    loadConversationDocuments(conversationId);
  }, [conversationId]);

  // Load templates when brand or expert changes
  useEffect(() => {
    if (selectedBrandId && selectedExpertId) {
      loadAvailableTemplates();
    }
  }, [selectedBrandId, selectedExpertId]);

  // Load pinned/favourite templates for the slim bar
  useEffect(() => {
    // Pins of a brand / expert that is no longer selected must not replace the new selection's chips (DT-1556)
    let stale = false;
    const loadPinnedTemplates = async () => {
      if (!selectedExpertId || !selectedBrandId) {
        setQuickAccessTemplates([]);
        setPinnedTemplateIds(new Set());
        return;
      }
      try {
        // Load pinned/favourite templates for the slim bar
        const pinnedResponse = await axios.get(`/api/chat/templates/pinned/${selectedBrandId}/${selectedExpertId}`);
        if (stale) return;
        if (pinnedResponse.data.pinned) {
          setQuickAccessTemplates(pinnedResponse.data.pinned || []);
          const pinnedIds = new Set(pinnedResponse.data.pinned.map(t => t.id));
          setPinnedTemplateIds(pinnedIds);
        }
      } catch (err) {
        console.error('Error loading pinned templates:', err);
        if (stale) return;
        setQuickAccessTemplates([]);
      }
    };
    loadPinnedTemplates();
    return () => { stale = true; };
  }, [selectedExpertId, selectedBrandId]);

  // 🔥 PHASE 5: Load conversation context when conversation changes
  useEffect(() => {
    if (conversationId) {
      loadConversationContext(conversationId);
    } else {
      setConversationContext(null);
    }
  }, [conversationId]);

  const checkMcpStatus = async () => {
    try {
      setLoadingMcpStatus(true);
      const response = await axios.get('/api/chat/mcp/status');
      setMcpStatus(response.data);
    } catch (error) {
      console.error('Error checking MCP status:', error);
      setMcpStatus({ available: false, error: error.message });
    } finally {
      setLoadingMcpStatus(false);
    }
  };

  // Load documents associated with a conversation
  const loadConversationDocuments = async (conversationId) => {
    if (!conversationId) {
      setConversationDocuments([]);
      return;
    }

    // A slow answer for the previous chat's conversation must not fill the new chat's list (DT-1536)
    const generation = chatGenerationRef.current;
    // Only the page-load request for the conversation the page opened with may replace the cached chat (DT-1531).
    // A later list (after an upload or a delete) of the same conversation shows the user's own chat by then.
    const opened = pageLoadRef.current;
    const pageLoadCache = conversationId === opened.conversationId ? opened.messages : undefined;
    try {
      setLoadingDocuments(true);
      const response = await axios.get(`/api/chat/documents/conversation/${conversationId}`);
      if (chatGenerationRef.current !== generation) return;
      setConversationDocuments(response.data.documents || []);
      console.log(`Loaded ${response.data.documents?.length || 0} documents for conversation ${conversationId}`);
    } catch (error) {
      console.error('Error loading conversation documents:', error);
      if (chatGenerationRef.current !== generation) return;
      setConversationDocuments([]);
      // A stored id the user may not use (another user's, or gone): drop it so the next send starts a new one (DT-1514)
      if (isConversationDenied(error)) {
        dropDeniedConversation(setConversationId, setMessages, conversationId, DOCUMENTS_CONVERSATION_DENIED_TEXT, 'documents',
          pageLoadCache);
      }
    } finally {
      if (pageLoadCache) pageLoadRef.current = { conversationId: null, messages: null };
      setLoadingDocuments(false);
    }
  };

  const loadTemplateStats = async () => {
    try {
      const response = await axios.get('/api/chat/template-stats');
      setTemplateStats(response.data);
    } catch (error) {
      console.error('Error loading template stats:', error);
    }
  };

  const handleQuickTemplateSelect = async (templateData) => {
    // Support both old query string format and new template execution format
    if (typeof templateData === 'string') {
      // Old format - just set the query in input
      setInput(templateData);
      return;
    }

    // New format - direct template execution
    if (templateData.type === 'template_execution') {
      await executeTemplateDirectly(templateData);
    }
  };

  const handleRecipeSelect = async (recipeData) => {
    // Handle recipe execution
    if (recipeData.type === 'recipe_execution' || recipeData.isRecipe) {
      await executeRecipeDirectly(recipeData);
      return;
    }
  };

  // Execute recipe with SSE streaming for real-time updates
  const executeRecipeDirectly = async (recipeData) => {
    if (!selectedBrandId) {
      alert('Please select a brand first');
      return;
    }

    const { templateId, templateName, parameters } = recipeData;

    console.log('[ChatInterface] 🧪 Executing recipe with SSE streaming:', templateId);

    // Add user message showing recipe execution
    const userMessage = {
      id: Date.now(),
      sender: 'user',
      text: `🧪 ${templateName}`,
      timestamp: new Date(),
      type: 'recipe-trigger'
    };

    // Writes below are dropped if the user resets the chat before the recipe answers (DT-1536)
    const run = beginChatRun();
    setMessages(prev => [...prev, userMessage]);
    setLoading(true);
    setResearchProgress({ step: 0, total: 4, currentAction: 'Starting recipe execution...' });

    try {
      // Use SSE streaming endpoint for recipe execution
      const response = await fetch('/api/recipes/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'x-auth-token': localStorage.getItem('token'),
          'Accept': 'text/event-stream'
        },
        body: JSON.stringify({
          recipe_id: templateId,
          parameters: parameters || {},
          conversation_id: conversationId,
          brand_id: selectedBrandId
        }),
        credentials: 'include'
      });

      console.log('📡 [RECIPE SSE] Response status:', response.status);
      console.log('📡 [RECIPE SSE] Response content-type:', response.headers.get('content-type'));

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Recipe execution failed: ${response.status} - ${errorText}`);
      }

      const ct = response.headers.get('content-type') || '';
      const isStream = ct.includes('text/event-stream');

      if (isStream && response.body) {
        console.log('🌊 [RECIPE SSE] Processing SSE stream');

        // Handle SSE stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        try {
          while (true) {
            const { value, done } = await reader.read();
            if (done) {
              console.log('🌊 [RECIPE SSE] Stream completed');
              break;
            }

            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;

            // Process complete events (separated by double newlines)
            const events = buffer.split('\n\n');
            buffer = events.pop() || '';

            for (const eventText of events) {
              if (!eventText.trim()) continue;

              const lines = eventText.split('\n');
              let eventData = null;

              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  try {
                    eventData = JSON.parse(line.slice(6));
                    break;
                  } catch (parseError) {
                    console.warn('🔧❌ SSE parse error:', parseError, 'for line:', line);
                  }
                }
              }

              if (eventData) {
                console.log('📊 [RECIPE SSE] Event:', eventData.type);

                // Capture conversationId and messageId from SSE events
                if (eventData.conversationId && !conversationId && run.stillThisChat()) {
                  console.log('🆔 [RECIPE SSE] Setting conversationId:', eventData.conversationId);
                  setConversationId(eventData.conversationId);
                  localStorage.setItem('currentConversationId', eventData.conversationId);
                }
                if (eventData.message_id && run.stillThisChat()) {
                  console.log('🆔 [RECIPE SSE] Setting lastMessageId:', eventData.message_id);
                  setLastMessageId(eventData.message_id);
                }

                switch (eventData.type) {
                  case 'RECIPE_START':
                    console.log('🚀 [RECIPE] Started:', eventData.recipe_name);
                    run.progress({
                      step: 1,
                      total: 4,
                      currentAction: `Starting recipe: ${eventData.recipe_name}`
                    });
                    break;

                  case 'QUERY_PHASE_START':
                    console.log('📊 [RECIPE] Query phase starting, total queries:', eventData.total_queries);
                    run.progress({
                      step: 2,
                      total: 4,
                      currentAction: `Executing ${eventData.total_queries} queries...`
                    });
                    break;

                  case 'QUERY_RESULT':
                    // Combined query results HTML from backend
                    console.log('📊 [RECIPE] Query results received:', eventData.row_count, 'total rows');
                    
                    // Add query results as a message immediately
                    if (eventData.html) {
                      run.addMessage({
                        id: Date.now(),
                        sender: 'assistant',
                        text: '',
                        html: eventData.html,
                        timestamp: new Date(),
                        type: 'html',
                        recipe_id: templateId,
                        date_ranges: eventData.date_ranges
                      });
                    }
                    
                    run.progress({
                      step: 3,
                      total: 4,
                      currentAction: 'Query results ready, processing...'
                    });
                    break;

                  case 'PROCESSING_START':
                    console.log('⚙️ [RECIPE] Python processing starting:', eventData.step_count, 'steps');
                    run.progress({
                      step: 3,
                      total: 4,
                      currentAction: `Processing data (${eventData.step_count} steps)...`
                    });
                    break;

                  case 'AI_START':
                    console.log('🤖 [RECIPE] AI analysis starting');
                    run.progress({
                      step: 4,
                      total: 4,
                      currentAction: 'Generating AI analysis...'
                    });
                    break;

                  case 'AI_ANALYSIS':
                    console.log('🤖 [RECIPE] AI analysis received');
                    const analysis = eventData.analysis || {};
                    
                    // Add AI analysis as a separate message
                    if (analysis.content) {
                      run.addMessage({
                        id: Date.now(),
                        sender: 'assistant',
                        text: '',
                        html: `<div class="ai-analysis">${analysis.content}</div>`,
                        timestamp: new Date(),
                        type: 'html',
                        recipe_id: templateId,
                        ai_provider: analysis.provider
                      });
                    }
                    break;

                  case 'QUERY_ERROR':
                    console.error('❌ [RECIPE] Query error:', eventData.query_name, eventData.error);
                    run.addMessage({
                      id: Date.now(),
                      sender: 'assistant',
                      text: `Query "${eventData.query_name}" failed: ${eventData.error}`,
                      timestamp: new Date(),
                      type: 'error'
                    });
                    break;

                  case 'AI_ERROR':
                    console.error('❌ [RECIPE] AI error:', eventData.error);
                    run.addMessage({
                      id: Date.now(),
                      sender: 'assistant',
                      text: `AI analysis failed: ${eventData.error}`,
                      timestamp: new Date(),
                      type: 'error'
                    });
                    break;

                  case 'RECIPE_ERROR':
                    console.error('❌ [RECIPE] Execution error:', eventData.error);
                    throw new Error(eventData.error);

                  case 'RECIPE_COMPLETE':
                    console.log('✅ [RECIPE] Execution completed:', eventData.execution_id);
                    run.progress(null);
                    break;

                  default:
                    console.log('📦 [RECIPE] Unknown event type:', eventData.type);
                }
              }
            }
          }
        } catch (streamError) {
          console.error('❌ [RECIPE SSE] Stream error:', streamError);
          throw streamError;
        } finally {
          reader.releaseLock();
        }

        // Stream complete
        console.log('🌊 [RECIPE SSE] Stream processing finished');
        run.end();
        run.progress(null);

      } else {
        // Fallback: Non-streaming JSON response
        console.log('📄 [RECIPE] Processing JSON response (non-streaming fallback)');
        const data = await response.json();
        
        if (data.success) {
          // Capture IDs from JSON response
          if (data.conversationId && run.stillThisChat()) {
            setConversationId(data.conversationId);
            localStorage.setItem('currentConversationId', data.conversationId);
          }
          if (data.message_id && run.stillThisChat()) {
            setLastMessageId(data.message_id);
          }
          
          run.addMessage({
            id: Date.now(),
            sender: 'assistant',
            text: data.result?.ai_response || `Recipe "${templateName}" completed`,
            html: data.result?.html || '',
            timestamp: new Date(),
            type: 'html',
            recipe_id: templateId
          });
        } else {
          throw new Error(data.error || 'Recipe execution failed');
        }
        
        run.end();
        run.progress(null);
      }

    } catch (error) {
      console.error('[ChatInterface] ❌ Recipe execution error:', error);
      
      const errorMessage = {
        id: Date.now() + 1,
        sender: 'assistant',
        text: `Error executing recipe: ${error.message}`,
        timestamp: new Date(),
        type: 'error'
      };
      run.addMessage(errorMessage);
      run.end();
      run.progress(null);
    }
  };

  const executeTemplateDirectly = async (templateData) => {
    if (!selectedBrandId) {
      alert('Please select a brand first');
      return;
    }

    const { templateId, templateName, parameters, template_type } = templateData;

    // ⚡ USE STREAMING ENDPOINT FOR MULTI-QUERY TEMPLATES
    // Multi-query templates need SSE streaming for two-phase response (data first, then AI analysis)
    // Check template_type from database instead of hardcoded list
    const isMultiQuery = template_type === 'multi-query';
    const isRecipe = template_type === 'recipe';
    const isPlaybook = template_type === 'playbook';

    // 🧪 RECIPE HANDLING - Route to SSE streaming endpoint
    if (isRecipe) {
      console.log(`🧪 [STREAMING] Recipe template detected (type=${template_type}), routing to executeRecipeDirectly: ${templateId}`);
      await executeRecipeDirectly({
        templateId: templateId,
        templateName: templateName,
        parameters: parameters,
        isRecipe: true
      });
      return;
    }

    if (isMultiQuery || isPlaybook) {
      console.log(`⚡ [STREAMING] ${isPlaybook ? 'Playbook' : 'Multi-query'} template detected (type=${template_type}), using SSE endpoint: ${templateId}`);

      // Add user message showing template execution
      const userMessage = {
        id: Date.now(),
        sender: 'user',
        text: `📋 ${templateName}`,
        timestamp: new Date(),
        type: 'template-trigger'
      };

      // Writes below are dropped if the user resets the chat before the report answers (DT-1536)
      const run = beginChatRun();
      setMessages(prev => [...prev, userMessage]);
      setLoading(true);

      try {
        const response = await fetch('/api/chat/templates/execute', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'x-auth-token': localStorage.getItem('token'),
            'Accept': 'text/event-stream'
          },
          body: JSON.stringify({
            templateId: templateId,
            brandId: parseInt(selectedBrandId),
            expertId: selectedExpertId,
            parameters: parameters || {},
            conversationId: conversationId
          }),
          credentials: 'include'
        });

        console.log('🚀 [ORIGINAL ENDPOINT] Response status:', response.status);
        console.log('🚀 [ORIGINAL ENDPOINT] Response content-type:', response.headers.get('content-type'));

        if (!response.ok) {
          const failed = new Error(`Request failed: ${response.status} ${response.statusText}`);
          if (response.status === 403) {
            const denied = await response.clone().json().catch(() => ({}));
            failed.conversationDenied = denied.code === 'CONVERSATION_ACCESS_DENIED';
          }
          throw failed;
        }

        const ct = response.headers.get('content-type') || '';
        const isStream = ct.includes('text/event-stream');

        if (isStream && response.body) {
          console.log('✅ [ORIGINAL ENDPOINT] Processing SSE stream');

          // Handle SSE stream properly
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          try {
            while (true) {
              const { value, done } = await reader.read();
              if (done) {
                console.log('✅ [ORIGINAL ENDPOINT] Stream completed');
                break;
              }

              const chunk = decoder.decode(value, { stream: true });
              buffer += chunk;

              // Process complete events
              const events = buffer.split('\n\n');
              buffer = events.pop() || '';

              for (const eventText of events) {
                if (!eventText.trim()) continue;

                const lines = eventText.split('\n');
                let eventData = null;

                for (const line of lines) {
                  if (line.startsWith('data: ')) {
                    try {
                      eventData = JSON.parse(line.slice(6));
                      break;
                    } catch (parseError) {
                      console.warn('⚠️ SSE parse error:', parseError, 'for line:', line);
                    }
                  }
                }

                if (eventData) {
                  console.log('📦 [ORIGINAL ENDPOINT] SSE Event:', eventData.type);

                  if (eventData.type === 'PHASE_1_COMPLETE' && eventData.html) {
                    console.log('🔥 [PHASE 1] Adding HTML results');
                    console.log('📊 [PHASE 1 DEBUG] Event data keys:', Object.keys(eventData));
                    console.log('📊 [PHASE 1 DEBUG] HTML length:', eventData.html?.length);
                    console.log('📊 [PHASE 1 DEBUG] HTML preview:', eventData.html?.substring(0, 200));
                    console.log('📊 [PHASE 1 DEBUG] Results:', eventData.results?.length, 'results');
                    if (eventData.results) {
                      eventData.results.forEach((result, i) => {
                        console.log(`📊 [PHASE 1 DEBUG] Result ${i}:`, result.id, result.name, result.results?.length, 'rows');
                      });
                    }
                    run.addMessage({
                      id: Date.now(),
                      sender: 'assistant',
                      text: '',
                      html: eventData.html,
                      timestamp: new Date(),
                      type: 'html'
                    });
                  } else if (eventData.type === 'PHASE_2_COMPLETE' && eventData.aiAnalysis) {
                    console.log('🔥 [PHASE 2] Adding AI analysis as HTML');
                    run.addMessage({
                      id: Date.now(),
                      sender: 'assistant',
                      text: '',
                      html: eventData.aiAnalysis.content || '<div>AI analysis completed</div>',
                      timestamp: new Date(),
                      type: 'html'  // Changed from 'text' to 'html' to render properly
                    });
                  } else if (eventData.type === 'STREAMING_COMPLETE') {
                    console.log('🏁 [COMPLETE] Streaming finished');
                  } else if (eventData.type === 'STREAM_COMPLETE') {
                    // Agentic loop streaming response (prompt caching + token streaming)
                    console.log('🏁 [STREAM_COMPLETE] Agentic response received');
                    const responseText = eventData.response || eventData.aiResponse || '';
                    const responseHtml = eventData.html || null;
                    if (responseText || responseHtml) {
                      run.addMessage({
                        id: Date.now(),
                        sender: 'assistant',
                        text: responseText,
                        html: responseHtml,
                        timestamp: new Date(),
                        type: responseHtml ? 'html' : 'text'
                      });
                    }
                  }
                }
              }
            }
          } catch (streamError) {
            console.error('❌ [ORIGINAL ENDPOINT] SSE stream error:', streamError);
          } finally {
            reader.releaseLock();
          }

          // Stream is complete - set loading false
          console.log('✅ [ORIGINAL ENDPOINT] Stream processing finished, setting loading=false');
          run.end();
        } else {
          console.log('📄 [ORIGINAL ENDPOINT] Processing JSON response');
          const data = await response.json();
          run.addMessage({
            id: Date.now(),
            sender: 'assistant',
            text: data.aiResponse || 'Analysis completed',
            html: data.html,
            timestamp: new Date(),
            type: data.html ? 'html' : 'text'
          });

          run.end();
        }

      } catch (error) {
        console.error('❌ [ORIGINAL ENDPOINT] Request failed:', error);
        if (isConversationDenied(error)) {
          dropDeniedConversation(setConversationId, setMessages, conversationId, TEMPLATE_CONVERSATION_DENIED_TEXT, 'template');
        } else {
          run.addMessage({
            id: Date.now(),
            sender: 'assistant',
            text: `Error loading ${templateName}: ${error.message}. Please try again.`,
            timestamp: new Date(),
            type: 'error'
          });
        }

        run.end();
      }

      return;
    }

    // For single-query templates, continue using the direct template endpoint (preserves existing behavior)
    // This ensures all 36+ single-query templates continue working exactly as before
    console.log(`⚡ [DIRECT] Using direct template endpoint for single-query template: ${templateId}`);

    // Add user message showing template execution
    const userMessage = {
      id: Date.now(),
      sender: 'user',
      text: `📋 ${templateName}`,
      timestamp: new Date(),
      type: 'template-trigger'
    };

    // Writes below are dropped if the user resets the chat before the report answers (DT-1536)
    const run = beginChatRun();
    setMessages(prev => [...prev, userMessage]);
    setLoading(true);
    setResearchProgress({ step: 0, total: 1, currentAction: 'Executing template...' });

    try {
      console.log('[DEBUG] 🔵 Executing Quick Report with:', {
        templateId,
        brandId: selectedBrandId,
        expertId: selectedExpertId,
        conversationId: conversationId,
        parameters: parameters || {}
      });

      const response = await axios.post(`/api/chat/templates/${templateId}/execute`, {
        brandId: parseInt(selectedBrandId),
        parameters: parameters || {},
        expertId: selectedExpertId,
        conversationId: conversationId  // ✅ Pass conversationId for debug logging
      }, {
        timeout: 180000  // 3 minutes timeout for AI-powered template analysis
      });

      console.log('[Quick Report] Response:', {
        success: response.data.success,
        hasData: !!response.data.data,
        hasHtml: !!response.data.html,
        templateType: response.data.template_type,
        error: response.data.error
      });

      if (response.data.success) {
        // ✅ Capture messageId from response for debug logging
        if (response.data.message_id && run.stillThisChat()) {
          console.log('[DEBUG] ✅ Quick Report messageId:', response.data.message_id);
          setLastMessageId(response.data.message_id);
        }

        // Add assistant response with template results
        const assistantMessage = {
          id: Date.now() + 1,
          sender: 'assistant',
          text: `Template executed successfully: ${response.data.template_info?.name}`,
          timestamp: new Date(),
          type: 'dynamic-template-result', // ✅ FIX: Use same rendering as settings dialog
          data: response.data.data,
          html: response.data.html, // Include rendered HTML for multi-query templates
          template_type: response.data.template_type,
          template: response.data.template_info,
          parameters_used: response.data.parameters_used,
          dynamic_metadata: response.data.dynamic_metadata || {}, // Include metadata for component
          execution_metadata: response.data.execution_metadata || {
            execution_time: response.data.execution_time || 0,
            template_id: templateId,
            expert_id: selectedExpertId,
            has_dynamic_grouping: false
          },
          performance: {
            source: 'template',
            templateUsed: templateId,
            executionTime: response.data.execution_time,
            fastPath: true
          }
        };

        run.addMessage(assistantMessage);

        // No conversation id is made up here: /templates/:id/execute stores no conversation, and /message refuses an
        // id the caller has no stored conversation for (DT-1249). The next typed message creates a real one.

        // Template executed successfully - set loading false and clear progress
        run.end();
        run.progress(null);

        // 🔥 Use immediate context from response if available (no polling needed!); none once the chat was reset
        const reportStillShown = run.stillThisChat();
        if (reportStillShown && response.data.conversationContext?.hasContext) {
          console.log('🔥 [Template] Using context from immediate response (no 12s wait!)');
          console.log('🔥 [Template] Context data:', response.data.conversationContext.context);
          setConversationContext(response.data.conversationContext.context);
        } else if (reportStillShown && (conversationId || response.data.conversation_id)) {
          // Fallback: Poll for context after background extraction completes
          const contextConvId = conversationId || response.data.conversation_id;
          console.log('🕒 [Template] No immediate context - scheduling poll in 12 seconds for:', contextConvId);
          setTimeout(() => {
            console.log('🔄 [Template] Polling for context now...');
            if (run.stillThisChat()) loadConversationContext(contextConvId);
          }, 12000); // 12 seconds - gives background extraction time to complete
        }

      } else {
        // Handle error response (backend returns success: false)
        const errorMsg = response.data.error || response.data.details || 'Template execution failed';
        console.error('[Quick Report] Execution failed:', response.data);
        throw new Error(errorMsg);
      }

    } catch (error) {
      console.error('Template execution error:', error);
      if (isConversationDenied(error)) {
        dropDeniedConversation(setConversationId, setMessages, conversationId, TEMPLATE_CONVERSATION_DENIED_TEXT, 'quick report');
      } else {
        run.addMessage({
          id: Date.now() + 1,
          sender: 'assistant',
          text: `Sorry, I couldn't execute the ${templateName} template. ${error.response?.data?.details || error.message}`,
          timestamp: new Date(),
          type: 'error'
        });
      }

      // Error occurred - set loading false and clear progress
      run.end();
      run.progress(null);
    }
    // No finally block - loading is set to false in each path above
  };

  const loadAvailableBrands = async () => {
    try {
      setLoadingBrands(true);
      const response = await axios.get('/api/chat/brands');
      setAvailableBrands(response.data);

      // Auto-select first brand with data if available
      const brandWithData = response.data.find(brand => brand.hasData);
      if (brandWithData && !selectedBrandId) {
        setSelectedBrandId(brandWithData.id);
      }

      console.log('Available brands loaded:', response.data);
    } catch (error) {
      console.error('Error loading brands:', error);
      setAvailableBrands([]);
    } finally {
      setLoadingBrands(false);
    }
  };

  const loadBrandSchema = async (brandId) => {
    if (!brandId) return;

    try {
      const response = await axios.get(`/api/chat/schema/${brandId}`);
      setBrandSchema(response.data);
      console.log('Brand schema loaded:', response.data);
    } catch (error) {
      console.error('Error loading brand schema:', error);
      setBrandSchema(null);
    }
  };

  // Load experts for brand (Phase 1)
  const loadExperts = async (brandId) => {
    try {
      setExpertsLoading(true);
      console.log('[DEBUG] 🔍 Loading agents for brandId:', brandId);
      const resp = await axios.get(`/api/chat/agents/${brandId}`);
      // Experts of a brand that is no longer selected must not replace (or auto-select over) the new brand's (DT-1556)
      if (String(selectedBrandIdRef.current) !== String(brandId)) return;
      const list = resp.data?.experts || [];
      console.log('[DEBUG] 🔍 Loaded agents:', list.length, 'agents');
      setExperts(list);

      // Auto-select first expert if:
      // 1. No expert is currently selected, OR
      // 2. Currently selected expert is not in the new list (brand switch scenario)
      const currentExpertStillValid = list.some(ex => ex.id === selectedExpertId);
      console.log('[DEBUG] 🔍 Current expert still valid?', currentExpertStillValid, 'selectedExpertId:', selectedExpertId);

      if (list.length > 0 && (!selectedExpertId || !currentExpertStillValid)) {
        console.log('[DEBUG] ✅ Auto-selecting first expert:', list[0].name, list[0].id);
        setSelectedExpertId(list[0].id);
        localStorage.setItem('selectedExpertId', list[0].id);
      } else if (list.length === 0) {
        console.log('[DEBUG] ⚠️  No experts found, clearing selectedExpertId');
        setSelectedExpertId('');
        localStorage.removeItem('selectedExpertId');
      } else {
        console.log('[DEBUG] ✅ Keeping existing selectedExpertId:', selectedExpertId);
      }
    } catch (err) {
      console.error('[DEBUG] ❌ Error loading experts:', err);
      if (String(selectedBrandIdRef.current) !== String(brandId)) return;
      setExperts([]);
      setSelectedExpertId('');
      localStorage.removeItem('selectedExpertId');
    } finally {
      setExpertsLoading(false);
    }
  };

  const handleBrandChange = (event) => {
    const newBrandId = event.target.value;
    setSelectedBrandId(newBrandId);

    // Clear conversation when switching brands
    startNewChatGeneration();
    setMessages([{
      id: 1,
      sender: 'assistant',
      text: `Brand switched! I'm now ready to conduct comprehensive multi-step research for ${availableBrands.find(b => b.id == newBrandId)?.name || 'the selected brand'}. What insights would you like me to discover?`,
      timestamp: new Date(),
      type: 'text'
    }]);
    setConversationId(null);
    // Clear conversation ID from localStorage when switching brands
    localStorage.removeItem('currentConversationId');
    setResearchProgress(null);

    // Clear experts temporarily on brand change
    // They will be reloaded by the useEffect watching selectedBrandId
    // Don't clear selectedExpertId here - let loadExperts handle it properly
    console.log('[DEBUG] 🔄 Brand changed to:', newBrandId, '- experts will reload');
    setExperts([]);
  };

  const handleExpertChange = (event) => {
    const newExpertId = event.target.value;
    const expertName = (experts || []).find(ex => ex.id === newExpertId)?.name || 'the selected expert';
    setSelectedExpertId(newExpertId);

    // Clear conversation when switching experts
    startNewChatGeneration();
    setMessages([{
      id: 1,
      sender: 'assistant',
      text: `Switched to ${expertName}. How can I help you?`,
      timestamp: new Date(),
      type: 'text'
    }]);
    setConversationId(null);
    localStorage.removeItem('currentConversationId');
    setResearchProgress(null);
  };

  const handleInputChange = useCallback((e) => {
    setInput(e.target.value);
  }, []);

  // Helper function to generate title from message
  const generateTitleFromMessage = useCallback((message) => {
    if (!message || typeof message !== 'string') return 'New Conversation';
    const brandName = availableBrands.find(b => b.id === selectedBrandId)?.name || 'Brand';

    // Clean and truncate the message
    let title = `[${brandName}] ` + message.trim();

    // Remove common question words and phrases for cleaner titles
    title = title.replace(/^(can you|could you|please|would you|how do|what is|what are|show me|tell me|explain|analyze|compare|create|generate)/i, '');
    title = title.trim();

    // Capitalize first letter
    if (title.length > 0) {
      title = title.charAt(0).toUpperCase() + title.slice(1);
    }

    // Truncate to reasonable length
    if (title.length > 50) {
      title = title.substring(0, 47) + '...';
    }

    // Fallback if title becomes empty
    if (!title || title.length < 3) {
      title = 'New Conversation';
    }

    return title;
  }, [availableBrands, selectedBrandId]); // useCallback dependencies

  // Auto-generate conversation title based on first user message
  const generateConversationTitle = useCallback(async (convId, userMessage) => {
    if (!convId || !userMessage) return;

    try {
      // Generate a concise title from the user's first message
      const title = generateTitleFromMessage(userMessage);

      await axios.put(`/api/chat/conversations/${convId}`, {
        title: title
      });

      console.log('Auto-generated conversation title:', title);
    } catch (error) {
      console.error('Error generating conversation title:', error);
      // Don't show alert for this, as it's not critical
    }
  }, [generateTitleFromMessage]); // useCallback dependencies

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const loadConversations = useCallback(async (brandId) => {
    if (!brandId) return;

    try {
      setLoadingConversations(true);
      const response = await axios.get(`/api/chat/conversations?brandId=${brandId}`);
      // A list for a brand that is no longer selected must not replace the selected brand's list (DT-1556)
      if (String(selectedBrandIdRef.current) !== String(brandId)) return;
      // Sort conversations by most recent first (by updated_at or created_at)
      const sortedConversations = (response.data.conversations || []).sort((a, b) => {
        const aDate = new Date(a.updated_at || a.created_at || 0);
        const bDate = new Date(b.updated_at || b.created_at || 0);
        return bDate - aDate; // Most recent first
      });
      setConversations(sortedConversations);
      console.log('Conversations loaded:', sortedConversations);
    } catch (error) {
      console.error('Error loading conversations:', error);
      if (String(selectedBrandIdRef.current) !== String(brandId)) return;
      setConversations([]);
    } finally {
      setLoadingConversations(false);
    }
  }, []); // useCallback with no dependencies since it doesn't use any changing state

  // **NEW: Load conversation history for persistence**
  // GET /conversations/:id/history returns at most this many messages: the limit enhanced-chat-service passes to
  // chat-database-service.getConversationHistory (ORDER BY timestamp ASC LIMIT 50, so the oldest; DT-1525)
  const HISTORY_RESTORE_CAP = 50;

  // GET /conversations/:id/history answers { history, conversationId }; one mapping for the page-load restore and the
  // sidebar's loadConversation
  const historyToMessages = (history) => history.map(msg => ({
    id: msg.id || Date.now() + Math.random(),
    sender: msg.sender,
    text: msg.message_text || msg.content || '', // Handle both field names
    timestamp: new Date(msg.timestamp),
    type: msg.type || 'text',
    html: msg.message_html || msg.html,
    queryResults: msg.queryResults,
    researchSteps: msg.researchSteps,
    researchSummary: msg.researchSummary,
    summary: msg.summary,
    visualizations: msg.visualizations,
    hasQuery: msg.hasQuery
  }));

  // Page-load restore. It read response.data.messages, which the route never sends, so nothing was ever restored
  // (DT-1517). It fills the chat from the server only when this browser's copy (localStorage chatMessages) holds no
  // conversation, i.e. no message of the user's: the server copy is poorer (the oldest 50 messages, no stored
  // charts or query results), so it never replaces a fuller local chat. It also only fills a chat nothing changed in
  // while it loaded (the user may already have sent a message). A refused id is dropped like on the other chat paths.
  const loadConversationHistory = async (conversationId) => {
    if (!conversationId) return;
    const shownAtStart = messages;
    const localIsEmpty = !shownAtStart.some(msg => msg.sender === 'user');
    if (localIsEmpty) setRestoringHistory(true);

    try {
      console.log(`Loading conversation history for: ${conversationId}`);
      // a timeout so a hung request cannot leave the "Restoring" line up: it then fails like any other error
      const response = await axios.get(`/api/chat/conversations/${conversationId}/history`, { timeout: 30000 });
      const history = response.data?.history;

      if (!localIsEmpty) {
        console.log('Keeping the local copy of the conversation');
      } else if (Array.isArray(history) && history.length > 0) {
        const restored = historyToMessages(history);
        // The route returns at most HISTORY_RESTORE_CAP messages, the oldest ones (DT-1525): say so when it hits the cap
        if (history.length >= HISTORY_RESTORE_CAP) {
          restored.push({
            id: Date.now() + Math.random(), sender: 'assistant', timestamp: new Date(), type: 'text',
            text: `Only the first ${HISTORY_RESTORE_CAP} messages of this conversation could be restored.`
          });
        }
        setMessages(prev => (prev === shownAtStart ? restored : prev));
        console.log(`Restored ${restored.length} messages from conversation`);
      } else {
        console.log('No messages found in conversation history');
      }
    } catch (error) {
      if (isConversationDenied(error)) {
        // Not a conversation this login may open (e.g. another user's id left in this browser): start a new one, and
        // do not keep showing its cached messages. Same handler as the documents refusal, so only one warns (DT-1531).
        // the documents wording: a page-load refusal reads the same whichever request answers first (DT-1531 QA)
        dropDeniedConversation(setConversationId, setMessages, conversationId, DOCUMENTS_CONVERSATION_DENIED_TEXT, 'page-load restore', shownAtStart);
        return;
      }
      console.error('Error loading conversation history:', error);
      // Don't clear messages on error - keep current state
    } finally {
      setRestoringHistory(false);
    }
  };

  // 🔥 PHASE 5: Load conversation context
  const loadConversationContext = async (conversationId) => {
    if (!conversationId) {
      setConversationContext(null);
      return;
    }

    // A slow answer for the previous chat's conversation must not fill the new chat's context panel (DT-1536)
    const generation = chatGenerationRef.current;
    try {
      console.log('🔍 [Context] Fetching context for conversation:', conversationId);
      const response = await axios.get(`/api/chat/context/${conversationId}`);
      if (chatGenerationRef.current !== generation) return;
      if (response.data.hasContext) {
        setConversationContext(response.data.context);
        console.log('🔥 [Context] Loaded conversation context:', response.data.context);
      } else {
        setConversationContext(null);
      }
    } catch (error) {
      console.error('❌ [Context] Error loading context:', error);
      if (chatGenerationRef.current !== generation) return;
      setConversationContext(null);
    }
  };

  // 🔥 PHASE 5: Clear conversation context
  const handleClearContext = async () => {
    if (!conversationId) return;

    try {
      setContextLoading(true);
      await axios.delete(`/api/chat/context/${conversationId}`);
      setConversationContext(null);
      console.log('✅ [Context] Conversation context cleared');

      // Show success message
      const clearMessage = {
        id: Date.now(),
        sender: 'assistant',
        text: '✨ Context cleared! Starting fresh - I won\'t remember the previous filters or time periods.',
        timestamp: new Date(),
        type: 'text'
      };
      setMessages(prev => [...prev, clearMessage]);
    } catch (error) {
      console.error('❌ [Context] Error clearing context:', error);
      alert('Failed to clear context. Please try again.');
    } finally {
      setContextLoading(false);
    }
  };

  // The current conversation's id, creating the conversation first when this chat has none yet (a fresh chat, or
  // after a brand / expert switch or an access-denied reset). Message sends and document uploads both use it, so
  // neither ever sends a missing id ("null"), which the server refuses (DT-1294).
  // Callers that arrive while a creation is in flight (two quick drops, a drop during a send) share it instead of
  // creating a second conversation; kept per brand and dropped once the new id is in state.
  // Creates a conversation for the chat on screen now and shares it through creatingConversationRef, so a send made
  // while it runs waits for it instead of creating another (also used by Start New Conversation, DT-1561)
  const startConversationCreation = useCallback(async ({ refreshList = false } = {}) => {
    const generation = chatGenerationRef.current;
    const promise = (async () => {
      const response = await axios.post('/api/chat/conversations', {
        brandId: selectedBrandId,
        title: '' // Backend will generate default title
      });
      const newId = response.data.conversationId;
      // The chat was reset while this was being created: do not put this id over the new chat's (DT-1536)
      if (chatGenerationRef.current !== generation) return newId;
      // A conversation created just now has no history: skip the restore load, which could otherwise race this send
      // and replace what is on screen (e.g. a Quick Report result) with the stored messages (DT-1249)
      skipHistoryLoadRef.current = true;
      setConversationId(newId);
      localStorage.setItem('currentConversationId', newId);
      // The send path refreshes the sidebar after its reply; an upload-first conversation would otherwise be missing
      if (refreshList && selectedBrandId) setTimeout(() => loadConversations(selectedBrandId), 500);
      return newId;
    })();
    creatingConversationRef.current = { brandId: selectedBrandId, generation, promise };
    try {
      return await promise;
    } catch (error) {
      if (creatingConversationRef.current?.promise === promise) creatingConversationRef.current = null;
      throw error;
    }
  }, [selectedBrandId, loadConversations]);

  const ensureConversationId = useCallback(async ({ refreshList = false } = {}) => {
    if (conversationId) return conversationId;
    const pending = creatingConversationRef.current;
    // Shared only within the chat it was started for: after a reset (e.g. an expert switch, which keeps the brand and
    // a null id) the abandoned creation belongs to the old chat and must not take the new chat's messages (DT-1536)
    if (pending && pending.brandId === selectedBrandId && pending.generation === chatGenerationRef.current) {
      return pending.promise;
    }
    return startConversationCreation({ refreshList });
  }, [conversationId, selectedBrandId, startConversationCreation]);

  // Document uploads create the conversation through the same helper, and also refresh the sidebar list
  const ensureUploadConversationId = useCallback(
    () => ensureConversationId({ refreshList: true }), [ensureConversationId]);

  useEffect(() => {
    if (conversationId || creatingConversationRef.current?.brandId !== selectedBrandId) {
      creatingConversationRef.current = null;
    }
  }, [conversationId, selectedBrandId]);

  const handleSendMessage = useCallback(async () => {
    console.log('🚀 [DEBUG] handleSendMessage called');
    console.log('🚀 [DEBUG] input:', input);
    console.log('🚀 [DEBUG] loading:', loading);
    console.log('🚀 [DEBUG] FEATURE_EXPERTS:', FEATURE_EXPERTS);
    console.log('🚀 [DEBUG] selectedExpertId:', selectedExpertId);
    console.log('🚀 [DEBUG] selectedBrandId:', selectedBrandId);
    console.log('🚀 [DEBUG] conversationId:', conversationId);

    if (!input.trim() || loading) {
      console.log('❌ [DEBUG] Exiting early - no input or loading:', { input: input.trim(), loading });
      return;
    }

    if (!selectedBrandId) {
      console.log('❌ [DEBUG] No brand selected');
      alert('Please select a brand first');
      return;
    }

    // The reply belongs to the chat this send started in: after a reset its writes are dropped and its conversation id
    // is not adopted back (DT-1536)
    const run = beginChatRun();

    // CRITICAL: Ensure conversation exists before sending message
    let activeConversationId;
    try {
      activeConversationId = await ensureConversationId();
    } catch (error) {
      console.error('❌ [DEBUG] Failed to create conversation:', error);
      alert('Failed to create conversation. Please try again.');
      return;
    }
    if (!run.stillThisChat()) return; // the chat was reset while the conversation was being created: nothing is sent

    console.log("✅ [DEBUG] Proceeding with message send with conversationId:", activeConversationId);

    const userMessage = {
      id: Date.now(),
      sender: 'user',
      text: input.trim(),
      timestamp: new Date(),
      type: 'text'
    };

    setMessages(prev => [...prev, userMessage]);
    const originalInput = input.trim();
    setInput('');
    setLoading(true);
    setResearchProgress({ step: 0, total: 2, currentAction: 'Loading schema...' });

    // Helper: render AI analysis as standalone message (not concatenated)
    const renderAiInsightsMessage = (content, meta = {}) => {
      // Format the content - convert markdown-like syntax to HTML
      const formatContent = (text) => {
        if (!text) return '<p>No insights available.</p>';

        // First, aggressively clean up the raw text
        text = text
          // Remove markdown code fences (```html, ``` etc)
          .replace(/^\s*```\w*\s*/gm, '')
          .replace(/^\s*```\s*$/gm, '')
          // Remove common AI introductory phrases
          .replace(/^Of course\.\s*/i, '')
          .replace(/^As an expert data analyst[^,]*,\s*/i, '')
          .replace(/^Here is a comprehensive analysis[^:]*:\s*/i, '')
          .replace(/^Here's a comprehensive analysis[^:]*:\s*/i, '')
          .replace(/^Here is[^:]*:\s*/i, '')
          .replace(/^Here's[^:]*:\s*/i, '')
          .replace(/^I'll provide[^:]*:\s*/i, '')
          .replace(/^Let me provide[^:]*:\s*/i, '')
          .replace(/^Based on[^,]*,\s*/i, '')
          .replace(/^\*\*\*\s*/g, '')
          .replace(/formatted in HTML for clarity[^.]*\.\s*/i, '')
          // Remove standalone asterisks or separators at the start
          .replace(/^[\*\-\s]+/m, '')
          // Collapse multiple consecutive blank lines into max 2 newlines
          .replace(/\n{3,}/g, '\n\n')
          // Trim leading and trailing whitespace
          .trim();

        // CRITICAL FIX: If content already has proper HTML structure (div, table, html, etc),
        // skip markdown conversion to avoid wrapping divs in <p> tags which creates gaps
        const hasProperHtml = text.includes('<div') ||
          text.includes('<html') ||
          text.includes('<!DOCTYPE') ||
          (text.includes('<table') && text.includes('</table>')) ||
          (text.includes('<style') && text.includes('</style>'));
        if (hasProperHtml) {
          console.log('[renderAiInsightsMessage] Content has proper HTML, skipping markdown conversion');
          // Still clean up any remaining markdown artifacts and intro phrases
          return text
            .replace(/^\s*```\w*\s*/gm, '')
            .replace(/^\s*```\s*$/gm, '')
            .replace(/^Of course\.\s*/i, '')
            .replace(/^As an expert data analyst[^,]*,\s*/i, '')
            .replace(/^Here is a comprehensive analysis[^:]*:\s*/i, '')
            .replace(/^Here's a comprehensive analysis[^:]*:\s*/i, '')
            .replace(/formatted in HTML for clarity[^.]*\.\s*/i, '')
            .replace(/^\*\*\*\s*/g, '')
            .trim(); // Return as-is, already formatted
        }

        // Otherwise, convert markdown-like syntax to HTML
        let formatted = text
          // Convert markdown headers
          .replace(/^### (.+)$/gm, '<h3>$1</h3>')
          .replace(/^## (.+)$/gm, '<h2>$1</h2>')
          .replace(/^# (.+)$/gm, '<h2>$1</h2>')
          // Convert **bold**
          .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
          // Convert bullet points
          .replace(/^- (.+)$/gm, '<li>$1</li>')
          .replace(/^\* (.+)$/gm, '<li>$1</li>')
          // Convert numbered lists
          .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
          // Wrap consecutive <li> in <ul>
          .replace(/(<li>.*?<\/li>\n?)+/gs, '<ul>$&</ul>')
          // Convert pipes to table
          .replace(/\|(.+?)\|/g, (match) => {
            const cells = match.split('|').filter(c => c.trim());
            const isHeader = match.includes('---');
            if (isHeader) return '';
            const tag = cells[0].includes(':--') ? 'th' : 'td';
            return '<tr>' + cells.map(c => `<${tag}>${c.trim()}</${tag}>`).join('') + '</tr>';
          })
          // Wrap table rows
          .replace(/(<tr>.*?<\/tr>\n?)+/gs, '<table>$&</table>')
          // Convert double line breaks to paragraphs
          .split('\n\n')
          .map(para => {
            para = para.trim();
            if (!para) return '';
            // Exclude HTML blocks from paragraph wrapping
            if (para.startsWith('<h') || para.startsWith('<ul') || para.startsWith('<table') || para.startsWith('<div')) {
              return para;
            }
            return `<p>${para.replace(/\n/g, '<br>')}</p>`;
          })
          .filter(para => para) // Remove empty paragraphs
          .join('\n');

        return formatted;
      };

      // Ensure content is not null/undefined
      const safeContent = content || '';
      const formattedContent = formatContent(safeContent);

      // Build date range display if available
      let dateRangeHtml = '';
      if (meta.dateRanges) {
        dateRangeHtml = `
          <div style="background: rgba(255, 255, 255, 0.15); border-radius: 6px; padding: 12px; margin-bottom: 12px; border-left: 3px solid rgba(255, 255, 255, 0.5);">
            <div style="font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; opacity: 0.9;">📅 Date Ranges Used</div>
            <div style="font-size: 13px; line-height: 1.8;">
              <div><strong>Current Period:</strong> ${meta.dateRanges.current_period?.label || 'N/A'}</div>
              <div><strong>Previous Period:</strong> ${meta.dateRanges.previous_period?.label || 'N/A'}</div>
              <div><strong>Same Period Last Year:</strong> ${meta.dateRanges.last_year?.label || 'N/A'}</div>
            </div>
          </div>
        `;
      }

      // Debug logging
      console.log('[renderAiInsightsMessage] Content length:', safeContent.length);
      console.log('[renderAiInsightsMessage] Formatted content length:', formattedContent.length);
      console.log('[renderAiInsightsMessage] Has date ranges:', !!meta.dateRanges);

      const wrappedHtml = `
        <style>
          .ai-analysis-container {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 12px;
            padding: 16px;
            margin: 12px 0;
            box-shadow: 0 8px 24px rgba(102, 126, 234, 0.15);
            color: white;
          }
          .ai-analysis-header {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 12px;
            padding-bottom: 10px;
            border-bottom: 2px solid rgba(255, 255, 255, 0.2);
          }
          .ai-analysis-icon {
            font-size: 24px;
          }
          .ai-analysis-title {
            font-size: 20px;
            font-weight: 600;
            margin: 0;
            text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }
          .ai-analysis-content {
            background: rgba(255, 255, 255, 0.95);
            color: #2c3e50;
            padding: 16px;
            border-radius: 8px;
            font-size: 15px;
            line-height: 1.6;
            box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
          }
          .ai-analysis-content > *:first-child {
            margin-top: 0 !important;
          }
          .ai-analysis-content > *:last-child {
            margin-bottom: 0 !important;
          }
          .ai-analysis-content h2,
          .ai-analysis-content h3,
          .ai-analysis-content h4 {
            color: #667eea;
            margin-top: 16px;
            margin-bottom: 8px;
            font-weight: 600;
          }
          .ai-analysis-content h2 {
            font-size: 18px;
            border-bottom: 2px solid #667eea;
            padding-bottom: 6px;
          }
          .ai-analysis-content h3 {
            font-size: 16px;
          }
          .ai-analysis-content h4 {
            font-size: 15px;
          }
          .ai-analysis-content p {
            margin: 10px 0;
            text-align: left;
          }
          .ai-analysis-content strong {
            color: #667eea;
            font-weight: 600;
            background: rgba(102, 126, 234, 0.1);
            padding: 2px 6px;
            border-radius: 3px;
          }
          .ai-analysis-content ul,
          .ai-analysis-content ol {
            margin: 12px 0;
            padding-left: 24px;
          }
          .ai-analysis-content li {
            margin: 6px 0;
            line-height: 1.6;
          }
          .ai-analysis-content table {
            width: auto;
            margin: 16px 0;
            border-collapse: collapse;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          }
          .ai-analysis-content th {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 12px;
            text-align: left;
            font-weight: 600;
          }
          .ai-analysis-content td {
            padding: 10px 12px;
            border-bottom: 1px solid #e9ecef;
          }
          .ai-analysis-content tr:hover {
            background: rgba(102, 126, 234, 0.05);
          }
          .ai-analysis-content blockquote {
            border-left: 4px solid #667eea;
            padding-left: 16px;
            margin: 20px 0;
            font-style: italic;
            color: #6c757d;
            background: rgba(102, 126, 234, 0.05);
            padding: 12px 12px 12px 16px;
            border-radius: 0 4px 4px 0;
          }
          .ai-analysis-content code {
            background: #f8f9fa;
            padding: 2px 6px;
            border-radius: 3px;
            font-family: 'Courier New', monospace;
            font-size: 13px;
            color: #e83e8c;
          }
          .ai-analysis-content pre {
            background: #2d3748;
            color: #e2e8f0;
            padding: 16px;
            border-radius: 6px;
            overflow-x: auto;
            font-size: 13px;
            line-height: 1.6;
          }
          .ai-analysis-footer {
            margin-top: 12px;
            padding-top: 12px;
            border-top: 1px solid rgba(255, 255, 255, 0.2);
            font-size: 12px;
            opacity: 0.85;
            display: flex;
            align-items: center;
            gap: 6px;
          }
        </style>
        <div class="ai-analysis-container">
          <div class="ai-analysis-header">
            <div class="ai-analysis-icon">🤖</div>
            <h3 class="ai-analysis-title">AI-Powered Analysis</h3>
          </div>
          ${dateRangeHtml}
          <div class="ai-analysis-content">
            ${formattedContent || '<p style="color: #999; font-style: italic;">No analysis content available.</p>'}
          </div>
          <div class="ai-analysis-footer">
            <span>✨</span>
            <span>Analysis generated using advanced AI reasoning</span>
          </div>
        </div>
      `;
      run.addMessage({
        id: Date.now() + Math.random(),
        sender: 'assistant',
        text: '',
        timestamp: new Date(),
        type: 'html',
        html: wrappedHtml,
        ...meta
      });
    };

    try {
      // Optimized 2-step progress for faster responses
      const progressInterval = setInterval(() => {
        run.progress(prev => {
          if (!prev || prev.step >= 2) return prev;
          return {
            ...prev,
            step: prev.step + 1,
            currentAction: prev.step === 0 ? 'Analyzing query...' : 'Complete!'
          };
        });
      }, 1500); // Faster updates for 2-step process

      // Choose endpoint based on expert selection
      const endpoint = FEATURE_EXPERTS && selectedExpertId ? '/api/chat/message/expert' : '/api/chat/message';

      console.log('🎯 [DEBUG] Selected endpoint:', endpoint);
      console.log('🎯 [DEBUG] Will use expert endpoint?:', FEATURE_EXPERTS && selectedExpertId);

      // Add conversation documents context
      const documentContext = conversationDocuments.length > 0 ? {
        document_ids: conversationDocuments.map(doc => doc.id),
        document_count: conversationDocuments.length,
        has_documents: true
      } : { has_documents: false };

      // Determine if selected expert is actually an agent (loaded from mcp_agents)
      const selectedIsAgent = (experts || []).find(ex => ex.id === selectedExpertId)?.isAgent;
      const payload = FEATURE_EXPERTS && selectedExpertId ? {
        message: originalInput,
        conversationId: activeConversationId,  // Use the guaranteed conversationId
        brandId: selectedBrandId,
        ...(selectedIsAgent ? { agentId: selectedExpertId } : { expertId: selectedExpertId }),
        documentContext,
        aiProvider: aiProvider  // Add AI provider selection
      } : {
        message: originalInput,
        conversationId: activeConversationId,  // Use the guaranteed conversationId
        brandId: selectedBrandId,
        documentContext,
        aiProvider: aiProvider  // Add AI provider selection
      };

      // Expert and plain sends both go over fetch to the endpoint chosen above; the reply is SSE or JSON.
      // (Plain sends had no branch at all, so Send spun forever and nothing was sent: DT-1511.)
      {
        // Streaming over fetch with AbortController timeout
        const controller = new AbortController();
        const timeoutMs = 300000; // 5 minutes timeout for complex streaming queries (matches backend/nginx)
        const timeoutId = setTimeout(() => {
          console.error('⏱️ [SSE Timeout] Request exceeded', timeoutMs / 1000, 'seconds');
          controller.abort();
        }, timeoutMs);

        // Build headers with auth token if available
        const fetchHeaders = { 'Content-Type': 'application/json' };
        try {
          const t = localStorage.getItem('token');
          if (t) {
            fetchHeaders['Authorization'] = `Bearer ${t}`;
            fetchHeaders['x-auth-token'] = t;
          }
        } catch (_) { }

        // Hint server we expect SSE
        fetchHeaders['Accept'] = 'text/event-stream';

        console.log('🚀 [DEBUG] About to make fetch request');
        console.log('🚀 [DEBUG] URL:', endpoint);
        console.log('🚀 [DEBUG] Method: POST');
        console.log('🚀 [DEBUG] Payload:', payload);

        let response;
        try {
          response = await fetch(endpoint, {
            method: 'POST',
            headers: fetchHeaders,
            body: JSON.stringify(payload),
            signal: controller.signal,
            // Include credentials in case cookie-based auth is used
            credentials: 'include'
          });
          console.log('🟢 [SSE Debug] Fetch completed successfully');
        } catch (fetchError) {
          console.error('🔴 [SSE Debug] Fetch failed:', fetchError);
          // The server never answered: the catch below puts the typed message back (a timeout keeps its own text)
          if (fetchError.name !== 'AbortError') fetchError.sendFailed = true;
          throw fetchError;
        }

        clearTimeout(timeoutId);

        // The backend refuses a conversation id this user cannot access (DT-1249), e.g. one left in
        // localStorage by another login: hand it to the catch below, which drops the stored id.
        if (response.status === 403) {
          const denied = await response.clone().json().catch(() => ({}));
          if (denied.code === 'CONVERSATION_ACCESS_DENIED') {
            const deniedError = new Error(denied.error || 'Conversation not found or access denied');
            deniedError.conversationDenied = true;
            throw deniedError;
          }
        }

        // fetch does not throw on an error status the way axios did: throw here so the catch below shows the server's
        // reason (404 unknown expert, 429 duplicate, 500) instead of an empty reply bubble. Only a top-level string is
        // shown: the server's catch-all handler answers { error: { message: <raw exception text> } }.
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          const reason = [data.message, data.error].find(v => typeof v === 'string' && v);
          const httpError = new Error(reason || `Request failed with status code ${response.status}`);
          httpError.response = { status: response.status, data };
          httpError.reason = reason || '';
          httpError.sendFailed = true;
          throw httpError;
        }

        const ct = response.headers.get('content-type') || '';
        console.log('📋 [SSE Debug] Response headers:', {
          contentType: ct,
          status: response.status,
          statusText: response.statusText,
          allHeaders: Array.from(response.headers.entries())
        });

        // Only treat as stream if content-type explicitly indicates SSE
        const isStream = ct.includes('text/event-stream');

        console.log('🔍 [SSE Debug] Stream detection:', {
          contentType: ct,
          isStream: isStream,
          hasBody: !!response.body,
          willUseStreamProcessing: isStream && !!response.body
        });

        if (isStream && response.body) {
          console.log('✅ [SSE Debug] Processing as SSE stream...');
          // Parse Server-Sent Events from ReadableStream (robust to CRLF and multiline data)
          const reader = response.body.getReader();
          const decoder = new TextDecoder('utf-8');
          let buffer = '';

          const splitEvents = (buf) => {
            // Split on double newlines (\n\n or \r\n\r\n)
            return buf.split(/\r?\n\r?\n/);
          };

          const parseEvent = (eventText) => {
            // Extract all data: lines, join them in case of multiline payloads
            const lines = eventText.split(/\r?\n/);
            const dataLines = lines
              .map(l => l.replace(/\r$/, ''))
              .filter(l => l.startsWith('data:'))
              .map(l => l.replace(/^data:\s?/, ''));
            if (dataLines.length === 0) return null;
            const payload = dataLines.join('\n');
            try {
              return JSON.parse(payload);
            } catch (e) {
              console.warn('Failed to JSON.parse SSE data payload:', payload);
              return null;
            }
          };

          let phase1Shown = false;

          const handleUpdate = (update) => {
            if (!update || !update.type) return;
            if (!run.stillThisChat()) {
              // The chat was reset: nothing of this reply is shown and its ids are not adopted. Only the old
              // conversation's title is still generated, server-side, for that conversation.
              if (update.type === 'EXPERT_COMPLETE' && update.conversationId && !conversationId) {
                generateConversationTitle(update.conversationId, originalInput).catch(err => {
                  console.error('[SSE] Failed to generate conversation title:', err);
                });
              }
              return;
            }
            // Debug log to ensure both phases reach the browser
            console.log('[SSE] update.type =', update.type);
            console.log('[SSE] Full update object:', update);

            // 🔥 CRITICAL FIX: Capture conversationId and messageId from ALL SSE events
            if (update.conversationId) {
              console.log('[SSE] ✅ Setting conversationId from stream:', update.conversationId);
              console.log('[SSE] 📍 Current conversationId before update:', conversationId);
              setConversationId(update.conversationId);
              localStorage.setItem('currentConversationId', update.conversationId);
              console.log('[SSE] 📍 setConversationId called, debug icon should enable now');
            }
            if (update.message_id || update.messageId) {
              const msgId = update.message_id || update.messageId;
              console.log('[SSE] ✅ Setting lastMessageId from stream:', msgId);
              console.log('[SSE] 📍 Current lastMessageId before update:', lastMessageId);
              setLastMessageId(msgId);
              console.log('[SSE] 📍 setLastMessageId called');
            }

            // Handle CONNECTION_ESTABLISHED (first event with IDs)
            if (update.type === 'CONNECTION_ESTABLISHED') {
              console.log('[SSE] ✅ Connection established, IDs received immediately');
              return; // Don't process further, just capture IDs
            }

            // ========================================================================
            // RECIPE EVENT HANDLERS
            // ========================================================================

            // Handle RECIPE_START
            if (update.type === 'RECIPE_START') {
              console.log('[SSE] 🍳 Recipe execution started:', update.recipe_name);
              setResearchProgress({ step: 1, total: 3, currentAction: 'Running queries...' });
              return;
            }

            // Handle QUERY_PHASE_START (all queries starting)
            if (update.type === 'QUERY_PHASE_START') {
              console.log('[SSE] 📊 Query phase started:', update.total_queries, 'queries');
              setResearchProgress({ step: 1, total: 3, currentAction: `Running ${update.total_queries} queries...` });
              return;
            }

            // Handle QUERY_RESULT (combined results from all queries)
            if (update.type === 'QUERY_RESULT') {
              console.log('[SSE] 📊 Query results received:', update.query_name);
              console.log('[SSE] Query details:', {
                query_id: update.query_id,
                row_count: update.row_count,
                total_queries: update.total_queries,
                has_html: !!update.html,
                html_length: update.html?.length || 0
              });

              if (update.html) {
                console.log('[SSE] ✅ Displaying combined query results');
                setMessages(prev => [...prev, {
                  id: Date.now() + Math.random(),
                  sender: 'assistant',
                  text: '',
                  timestamp: new Date(),
                  type: 'html',
                  html: update.html,
                  templateName: update.template_name || update.query_name,
                  provider: 'template-engine',
                  hasQuery: true,
                  queryId: update.query_id,
                  rowCount: update.row_count,
                  totalQueries: update.total_queries
                }]);
                phase1Shown = true;
              } else {
                console.warn('[SSE] ⚠️ Query results have no HTML to display:', update.query_name);
                // Still show a message indicating queries completed
                setMessages(prev => [...prev, {
                  id: Date.now() + Math.random(),
                  sender: 'assistant',
                  text: `Queries completed: ${update.total_queries || 0} queries, ${update.row_count || 0} total rows`,
                  timestamp: new Date(),
                  type: 'text'
                }]);
              }
              return;
            }

            // Handle PROCESSING_START
            if (update.type === 'PROCESSING_START') {
              console.log('[SSE] ⚙️ Python processing started');
              setResearchProgress({ step: 2, total: 3, currentAction: 'Processing data...' });
              return;
            }

            // Handle AI_START (recipe-level AI)
            if (update.type === 'AI_START') {
              console.log('[SSE] 🤖 Recipe AI analysis started');
              setResearchProgress({ step: 3, total: 3, currentAction: 'Generating insights...' });
              return;
            }

            // Handle AI_ANALYSIS (recipe-level AI complete)
            if (update.type === 'AI_ANALYSIS') {
              console.log('[SSE] ✅ Recipe AI analysis complete');
              console.log('[SSE] Analysis object:', update.analysis);
              console.log('[SSE] Analysis type:', typeof update.analysis);
              console.log('[SSE] Analysis keys:', update.analysis ? Object.keys(update.analysis) : 'null');
              setResearchProgress(null);
              if (update.analysis) {
                // Extract content - handle both object and string formats
                let content = typeof update.analysis === 'string'
                  ? update.analysis
                  : (update.analysis.content || update.analysis.text || update.analysis.response || update.analysis.analysis || '');

                console.log('[SSE] Extracted content length:', content ? content.length : 0);
                console.log('[SSE] Content preview (first 500 chars):', content ? content.substring(0, 500) : 'EMPTY');
                console.log('[SSE] Date ranges:', update.date_ranges);

                if (content && content.trim().length > 0) {
                  console.log('[SSE] Rendering AI insights with content length:', content.length);
                  renderAiInsightsMessage(content, {
                    provider: update.analysis.provider || 'gemini',
                    dateRanges: update.date_ranges
                  });
                } else {
                  console.warn('[SSE] ⚠️ AI analysis has no content or is empty');
                  console.warn('[SSE] Full analysis object:', JSON.stringify(update.analysis, null, 2));
                  // Still render with empty content to show date ranges
                  renderAiInsightsMessage('No analysis content available. Please check the AI service logs.', {
                    provider: update.analysis.provider || 'gemini',
                    dateRanges: update.date_ranges
                  });
                }
              } else {
                console.warn('[SSE] ⚠️ AI analysis object is null or undefined');
              }
              return;
            }

            // Handle RECIPE_COMPLETE
            if (update.type === 'RECIPE_COMPLETE') {
              console.log('[SSE] ✅ Recipe execution completed');
              setResearchProgress(null);
              return;
            }

            if (update.type === 'PHASE_1_COMPLETE') {
              // Immediate HTML message from DB queries
              setMessages(prev => [...prev, {
                id: Date.now() + Math.random(),
                sender: 'assistant',
                text: '',
                timestamp: new Date(),
                type: 'html',
                html: update.html,
                templateName: update.templateName,
                provider: 'template-engine',
                hasQuery: true
              }]);
              phase1Shown = true;
              // Clear progress on first phase completion
              setResearchProgress(null);
            } else if (update.type === 'AI_ANALYSIS_STARTING') {
              setResearchProgress({ step: 1, total: 2, currentAction: 'Generating AI insights...' });
            } else if (update.type === 'PHASE_2_COMPLETE') {
              // Debug: log AI analysis payload
              console.log('[SSE] PHASE_2_COMPLETE payload', {
                hasAnalysis: !!update.aiAnalysis,
                contentPreview: update.aiAnalysis?.content?.slice?.(0, 120),
                error: update.aiAnalysisError,
                hasUpdatedHtml: !!update.updatedHtml,
                toolsUsed: update.toolsUsed
              });
              // Capture tools used for header display (deduplicated)
              if (Array.isArray(update.toolsUsed)) {
                setLastToolsUsed([...new Set(update.toolsUsed)]);
              }
              // If Phase 1 never rendered (due to header/proxy issues), render the updated HTML now
              if (!phase1Shown && update.updatedHtml) {
                setMessages(prev => [...prev, {
                  id: Date.now() + Math.random(),
                  sender: 'assistant',
                  text: '',
                  timestamp: new Date(),
                  type: 'html',
                  html: update.updatedHtml,
                  templateName: update.templateName,
                  provider: 'template-engine',
                  hasQuery: true
                }]);
                phase1Shown = true;
              }
              // Append AI insights as a separate message (not concatenated)
              const aiProviderLabel = update.aiProvider || (update.toolsUsed ? 'mcp/claude' : 'gemini');
              let contentForUi = update.aiAnalysisError ? `<div class="error">${update.aiAnalysisError}</div>` : (update.aiAnalysis?.content || '');

              // Clean up markdown code fences and extra whitespace
              contentForUi = contentForUi
                .replace(/^\s*```\w*\s*/gm, '') // Remove markdown code fences at line start
                .replace(/\s*```\s*$/gm, '') // Remove markdown code fences at line end
                .replace(/^\s+/gm, '') // Remove leading spaces from each line
                .replace(/\n{3,}/g, '\n\n') // Collapse excessive blank lines
                .trim();

              // NOTE: The renderAiInsightsMessage function handles markdown conversion
              // We should NOT do any HTML conversion here - just pass the content through
              // The AI prompts are now designed to return proper HTML directly

              renderAiInsightsMessage(contentForUi, { provider: aiProviderLabel });
            } else if (update.type === 'EXPERT_COMPLETE') {
              // Generate conversation title if this is a new conversation
              if (update.conversationId && !conversationId) {
                console.log('[SSE] New conversation created, generating title');
                generateConversationTitle(update.conversationId, originalInput).catch(err => {
                  console.error('[SSE] Failed to generate conversation title:', err);
                });
                if (selectedBrandId) {
                  setTimeout(() => { if (run.stillThisChat()) loadConversations(selectedBrandId); }, 500);
                }
              }
            }
          };

          const processChunk = (text) => {
            console.log('[SSE Debug] Received chunk:', {
              chunkSize: text.length,
              timestamp: new Date().toISOString(),
              chunkPreview: text.substring(0, 100)
            });

            buffer += text;
            const parts = splitEvents(buffer);
            // Keep the last partial chunk in buffer
            buffer = parts.pop() || '';

            console.log('[SSE Debug] Processing', parts.length, 'complete events');

            for (const part of parts) {
              const update = parseEvent(part);
              if (update) {
                console.log('[SSE Debug] Parsed event:', update.type, 'at', new Date().toISOString());
                handleUpdate(update);
              }
            }
          };

          // Read the stream
          console.log('[SSE Debug] Starting to read stream at', new Date().toISOString());
          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) {
                console.log('[SSE Debug] Stream completed at', new Date().toISOString());
                break;
              }
              console.log('[SSE Debug] Raw chunk received:', value.length, 'bytes at', new Date().toISOString());
              try {
                processChunk(decoder.decode(value, { stream: true }));
              } catch (chunkError) {
                console.error('[SSE Debug] Error processing chunk:', chunkError);
                console.error('[SSE Debug] Problematic chunk:', decoder.decode(value));
                // Continue processing other chunks
              }
            }
            // Flush any remaining buffered text
            if (buffer.trim().length > 0) {
              try {
                processChunk('');
              } catch (flushError) {
                console.error('[SSE Debug] Error flushing buffer:', flushError);
              }
            }
          } catch (streamError) {
            console.error('[SSE Debug] Stream reading error:', streamError);
            throw streamError; // Re-throw to be caught by outer catch
          } finally {
            reader.releaseLock?.();
          }

          clearInterval(progressInterval);
          run.progress(null);
        } else {
          // Fallback: non-streaming JSON response (existing behavior)
          console.log('⚠️ [SSE Debug] Using JSON fallback - not a stream');
          console.log('📝 [SSE Debug] Reason:', {
            contentType: ct,
            isStream: isStream,
            hasBody: !!response.body
          });
          const data = await response.json();
          if (!run.stillThisChat()) {
            // The chat was reset: nothing of this reply is shown and its ids are not adopted (DT-1536)
            clearInterval(progressInterval);
            if (data.conversationId && !conversationId) {
              generateConversationTitle(data.conversationId, originalInput).catch(err => {
                console.error('[JSON Fallback] Failed to generate conversation title:', err);
              });
            }
            return;
          }
          console.log('📦 [SSE Debug] JSON response received:', {
            hasAiResponse: !!data.aiResponse,
            hasHtml: !!data.html,
            type: data.type,
            provider: data.provider,
            conversationId: data.conversationId,
            messageId: data.message_id || data.messageId,
            toolsUsed: data.performance?.toolsUsed || data.toolsUsed,
            allKeys: Object.keys(data)
          });

          // Capture tools used (deduplicated)
          if (Array.isArray(data?.performance?.toolsUsed)) {
            setLastToolsUsed([...new Set(data.performance.toolsUsed)]);
          } else if (Array.isArray(data?.toolsUsed)) {
            setLastToolsUsed([...new Set(data.toolsUsed)]);
          }

          clearInterval(progressInterval);
          run.progress(null);

          const aiMessage = {
            id: Date.now() + 1,
            sender: 'assistant',
            text: data.aiResponse,
            timestamp: new Date(),
            type: data.type || 'text',
            html: data.html,
            queryResults: data.queryResults,
            researchSteps: data.researchSteps,
            researchSummary: data.researchSummary,
            summary: data.summary,
            expert: data.expert,
            provider: data.provider,
            autoRouted: data.autoRouted,
            routingReason: data.routingReason,
            cacheType: data.cacheType,
            responseTime: data.responseTime,
            visualizations: data.visualizations,
            hasQuery: data.hasQuery,
            performance: data.performance,
            templateName: data.templateName,
            confidence: data.confidence
          };

          run.addMessage(aiMessage);
          const newConversationId = data.conversationId;
          console.log('🆔 [JSON Fallback] Setting conversationId:', newConversationId);
          setConversationId(newConversationId);
          if (newConversationId) {
            localStorage.setItem('currentConversationId', newConversationId);
            console.log('💾 [JSON Fallback] Saved to localStorage');
          }
          if (data.message_id || data.messageId) {
            const msgId = data.message_id || data.messageId;
            console.log('🆔 [JSON Fallback] Setting messageId:', msgId);
            setLastMessageId(msgId);
          }

          // 🔥 NEW: Use conversation context from response for immediate UI display
          if (data.conversationContext?.hasContext) {
            console.log('🔥 [JSON Fallback] Using conversation context from response');
            setConversationContext(data.conversationContext.context);
          } else if (activeConversationId) {
            // 🔥 Fallback: poll for context if not included in response
            setTimeout(() => { if (run.stillThisChat()) loadConversationContext(activeConversationId); }, 500);
          }

          if (data.conversationId && !conversationId) {
            await generateConversationTitle(data.conversationId, originalInput);
            if (selectedBrandId) setTimeout(() => { if (run.stillThisChat()) loadConversations(selectedBrandId); }, 500);
          }
        }

        // Message sent successfully - set loading false
        run.end();
      } // Close send block

    } catch (error) {
      console.error('❌ Error sending message:', error);
      console.error('❌ Error stack:', error.stack);
      console.error('❌ Error type:', error.constructor.name);
      // The chat was reset meanwhile: the failure is not shown there, no id is adopted or dropped, and the box the
      // user may already be typing in is not overwritten with the old message (DT-1536)
      if (!run.stillThisChat()) return;
      setResearchProgress(null);

      // ✅ Try to extract conversationId from error response to enable debug button
      try {
        if (error.response && error.response.data) {
          const errorData = error.response.data;
          console.log('🆔 [Error] Checking for conversationId in error response:', errorData);
          if (errorData.conversationId) {
            console.log('✅ [Error] Found conversationId, enabling debug button:', errorData.conversationId);
            setConversationId(errorData.conversationId);
            localStorage.setItem('currentConversationId', errorData.conversationId);
          }
          if (errorData.messageId) {
            setLastMessageId(errorData.messageId);
          }
        }
      } catch (extractError) {
        console.warn('Failed to extract conversationId from error:', extractError);
      }

      // Provide specific error message based on error type
      let errorText = `I had trouble formatting the response: ${error.message}. Please check the console for details and try again.`;
      let errorSeverity;

      if (error.conversationDenied || error.response?.data?.code === 'CONVERSATION_ACCESS_DENIED') {
        setConversationId(null);
        localStorage.removeItem('currentConversationId');
        // Put the typed message back so the user can resend it without retyping (DT-1501)
        setInput(originalInput);
        setRefocusInput(n => n + 1);
        errorSeverity = 'warning';
        errorText = 'That conversation is not available to you, so a new one will start. Your message is back in the box: press Send to try again.';
      } else if (error.name === 'AbortError') {
        errorText = '⏱️ The query is taking longer than expected (timeout after 5 minutes). The analysis might be running complex calculations. Please try a simpler query or wait a moment and try again.';
        console.error('🔴 Request timed out - AbortError');
      } else if (error.sendFailed) {
        // Nothing was answered: put the typed message back, as for a refused conversation above (DT-1511)
        setInput(originalInput);
        setRefocusInput(n => n + 1);
        errorText = error.response
          ? `Your message could not be sent (status ${error.response.status})${error.reason ? `: ${error.reason}` : ''}. It is back in the box: press Send to try again.`
          : '🌐 Network error: your message could not be sent because the server could not be reached. It is back in the box: press Send to try again.';
      } else if (error.message?.includes('fetch') || error.message?.includes('network')) {
        errorText = '🌐 Network error: Unable to reach the server. Please check your connection and try again.';
      }

      const errorMessage = {
        id: Date.now() + 1,
        sender: 'assistant',
        text: errorText,
        timestamp: new Date(),
        type: 'error',
        severity: errorSeverity
      };

      setMessages(prev => [...prev, errorMessage]);

      // Error occurred - set loading false
      run.end();
    }
    // No finally block - loading is set to false in each path above
  }, [input, loading, selectedBrandId, conversationId, selectedExpertId, conversationDocuments, aiProvider, FEATURE_EXPERTS, loadConversations, generateConversationTitle, ensureConversationId, beginChatRun]); // useCallback dependencies

  // Send a message programmatically (e.g. from "Create Dashboard" button)
  const pendingSendRef = useRef(null);
  const sendProgrammaticMessage = useCallback((text) => {
    if (loading || !text) return;
    pendingSendRef.current = text;
    setInput(text);
  }, [loading]);

  // Auto-send when pendingSendRef is set and input matches
  useEffect(() => {
    if (pendingSendRef.current && input === pendingSendRef.current && !loading) {
      pendingSendRef.current = null;
      handleSendMessage();
    }
  }, [input, loading, handleSendMessage]);

  useEffect(() => {
    if (refocusInput) messageInputRef.current?.focus();
  }, [refocusInput]);

  // Conversation management functions
  const loadConversation = async (conversationId) => {
    // The list stays open and clickable while a history loads. Only the latest click may be applied, whichever answers
    // first, and only while no other reset (e.g. Start New) came after it (DT-1561 sweep)
    const run = beginChatRun();
    const call = ++loadConversationCallRef.current;
    const stillLatest = () => loadConversationCallRef.current === call && run.stillThisChat();
    try {
      setLoading(true);
      const response = await axios.get(`/api/chat/conversations/${conversationId}/history`);
      if (!stillLatest()) return;
      const conversation = response.data;

      console.log('Conversation data received:', response.data);

      if (response.data && response.data.history) {
        const formattedMessages = historyToMessages(response.data.history);

        console.log('Formatted messages:', formattedMessages);

        startNewChatGeneration();
        setMessages(formattedMessages);
        setConversationId(conversationId);
        // Store conversation ID in localStorage for persistence
        localStorage.setItem('currentConversationId', conversationId);
        setDrawerOpen(false);
      } else {
        console.warn('No conversation history found in response:', response.data);
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
      if (stillLatest()) alert('Failed to load conversation');
    } finally {
      // A reset (including this load's own, on success) has already ended the busy state; end() leaves a newer run's,
      // and a superseded click leaves the busy state to the latest one
      if (loadConversationCallRef.current === call) run.end();
    }
  };

  const createNewConversation = async () => {
    // Validate brand is selected
    if (!selectedBrandId) {
      console.warn('[New Conversation] Cannot create conversation without brand selection');
      alert('Please select a brand first');
      return;
    }

    let generation = null;
    try {
      console.log('[New Conversation] Creating new conversation for brand:', selectedBrandId);

      // Clear persisted state for fresh start
      localStorage.removeItem('currentConversationId');
      localStorage.removeItem('chatMessages');

      // End the old chat at once, before the create POST: a send made while it runs must not go to the old
      // conversation, and its answer must not be wiped when the POST returns (DT-1561)
      startNewChatGeneration();
      generation = chatGenerationRef.current;
      setConversationId(null);
      setMessages([{
        id: Date.now(),
        sender: 'assistant',
        text: `Ready for a new conversation! I can help you analyze ${availableBrands.find(b => b.id == selectedBrandId)?.name || 'your'} data with comprehensive multi-step research. What would you like to explore?`,
        timestamp: new Date(),
        type: 'text'
      }]);
      setResearchProgress(null);
      setDrawerOpen(false);

      // Shared with a send made meanwhile; sets the id (and refreshes the list) only while this chat is still on
      // screen, so a POST that returns after another reset leaves the newer chat alone
      const newConversationId = await startConversationCreation({ refreshList: true });
      console.log('[New Conversation] ✅ Conversation ready with ID:', newConversationId);
    } catch (error) {
      console.error('[New Conversation] ❌ Failed to create conversation:', error);
      if (chatGenerationRef.current === generation) {
        alert('Failed to create new conversation. Please try again.');
      }
    }
  };

  const handleContextMenu = (event, conversation) => {
    event.preventDefault();
    setContextMenuAnchor(event.currentTarget);
    setSelectedConversationForMenu(conversation);
  };

  const handleCloseContextMenu = () => {
    setContextMenuAnchor(null);
    setSelectedConversationForMenu(null);
  };

  const handleUpdateConversationTitle = async (newTitle) => {
    if (!selectedConversationForMenu) return;

    try {
      await axios.put(`/api/chat/conversations/${selectedConversationForMenu.id}`, {
        title: newTitle
      });

      // Refresh conversations list
      loadConversations(selectedBrandId);
      handleCloseContextMenu();
    } catch (error) {
      console.error('Error updating conversation title:', error);
      alert('Failed to update conversation title');
    }
  };

  const handleArchiveConversation = async () => {
    if (!selectedConversationForMenu) return;

    try {
      await axios.delete(`/api/chat/conversations/${selectedConversationForMenu.id}`);

      // If current conversation is being archived, start a new one
      if (conversationId === selectedConversationForMenu.id) {
        createNewConversation();
      }

      // Refresh conversations list
      loadConversations(selectedBrandId);
      handleCloseContextMenu();
    } catch (error) {
      console.error('Error archiving conversation:', error);
      alert('Failed to archive conversation');
    }
  };

  const getProgressMessage = (step) => {
    const messages = [
      'Analyzing your question...',
      'Exploring data structure...',
      'Running initial queries...',
      'Gathering detailed insights...',
      'Cross-referencing data sources...',
      'Calculating key metrics...',
      'Generating visualizations...',
      'Finalizing comprehensive analysis...'
    ];
    return messages[step - 1] || 'Processing...';
  };

  // Memoized renderMessage function to prevent recreating on every render
  const renderMessage = useCallback((message) => {
    // Render message content

    if (message.sender === 'user') {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Paper sx={{
            p: 2,
            maxWidth: '70%',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            borderRadius: '18px 18px 4px 18px'
          }}>
            <Typography variant="body1">{message.text || '[Empty message]'}</Typography>
            <Typography variant="caption" sx={{ opacity: 0.8, mt: 1, display: 'block' }}>
              {message.timestamp ? message.timestamp.toLocaleTimeString() : 'Now'}
            </Typography>
          </Paper>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2, width: '100%' }}>
        <Paper sx={{
          p: 2,
          maxWidth: '80%',
          width: message.type === 'html' ? '100%' : 'auto', // Force full width for HTML
          minWidth: message.type === 'html' ? '100%' : 'auto', // Ensure minimum full width
          bgcolor: 'grey.100',
          borderRadius: '18px 18px 18px 4px'
        }}>
          {/* Research Progress Indicator */}
          {message.researchSteps && (
            <Card sx={{ mb: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <SearchIcon sx={{ mr: 1 }} />
                  <Typography variant="subtitle2">
                    Multi-Step Research Completed ({message.researchSteps} steps)
                  </Typography>
                </Box>
                {message.researchSummary && (
                  <ResearchSummary summary={message.researchSummary} />
                )}
              </CardContent>
            </Card>
          )}

          {/* Main Response */}
          {message.type === 'html' && message.html ? (
            <>
              <HTMLRenderer html={message.html} />
              {!loading && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1.5, justifyContent: 'flex-end' }}>
                  <Tooltip title="Create a live, shareable dashboard from these results">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DashboardIcon />}
                      onClick={() => sendProgrammaticMessage(
                        `Create a live dashboard from our conversation. Design it as a daily ready reckoner — key KPIs, important trends, and useful filters like date range. Make it visually impressive and distinctive.`
                      )}
                      sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                    >
                      Create Dashboard
                    </Button>
                  </Tooltip>
                </Box>
              )}
            </>
          ) : message.type === 'template-result' && (message.html || message.data) ? (
            <>
              {/* Template Result Display */}
              <Typography variant="body1" sx={{ mb: 2, color: 'success.main', fontWeight: 'bold' }}>
                ✅ {message.text}
              </Typography>

              {/* Execution Details */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip icon={<CheckCircleIcon />} label="Template" size="small" color="success" />
                {!message.html && message.data && (
                  <Chip icon={<AnalyticsIcon />} label={`${message.data.length} rows`} size="small" color="info" />
                )}
                <Chip label={`${message.execution_time}ms`} size="small" color="primary" />
                {message.template_type && (
                  <Chip label={message.template_type} size="small" color="default" />
                )}
              </Box>

              {/* Multi-query template HTML or single-query data table */}
              {message.html ? (
                <Box
                  sx={{
                    mt: 2,
                    '& .weekly-dashboard, & .products-dashboard, & .funnel-dashboard': {
                      fontFamily: 'inherit'
                    }
                  }}
                  dangerouslySetInnerHTML={{ __html: sanitizeRichHTML(message.html) }}
                />
              ) : message.data && message.data.length > 0 ? (
                <Box sx={{ mt: 2, overflowX: 'auto' }}>
                  <table style={{ width: 'auto', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5' }}>
                        {Object.keys(message.data[0]).map(key => (
                          <th key={key} style={{
                            padding: '8px 12px',
                            textAlign: 'left',
                            borderBottom: '2px solid #ddd',
                            fontWeight: 'bold',
                            textTransform: 'capitalize'
                          }}>
                            {key.replace(/_/g, ' ')}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {message.data.map((row, index) => (
                        <tr key={index} style={{
                          backgroundColor: index % 2 === 0 ? '#fff' : '#fafafa',
                          borderBottom: '1px solid #eee'
                        }}>
                          {Object.values(row).map((value, i) => (
                            <td key={i} style={{
                              padding: '8px 12px',
                              textAlign: typeof value === 'number' ? 'right' : 'left'
                            }}>
                              {typeof value === 'number' && value % 1 !== 0
                                ? value.toFixed(2)
                                : value}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              ) : null}
            </>
          ) : message.type === 'dynamic-template-result' ? (
            <>
              <DynamicTemplateResults
                data={message.data}
                template={message.template}
                parameters={message.parameters_used}
                dynamicMetadata={message.dynamic_metadata}
                executionMetadata={message.execution_metadata}
                html={message.html}
                template_type={message.template_type}
              />
              {/* Action buttons after data results */}
              {(message.data?.length > 0 || message.html) && !loading && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1.5, justifyContent: 'flex-end' }}>
                  <Tooltip title="Create a live, shareable dashboard from these results">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DashboardIcon />}
                      onClick={() => sendProgrammaticMessage(
                        `Create a live dashboard from our conversation. Design it as a daily ready reckoner — key KPIs, important trends, and useful filters like date range. Make it visually impressive and distinctive.`
                      )}
                      sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                    >
                      Create Dashboard
                    </Button>
                  </Tooltip>
                </Box>
              )}
            </>
          ) : message.type === 'template-execution' ? (
            <>
              <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">{message.text}</Typography>
                </Box>
                {message.parameters && Object.keys(message.parameters).length > 0 && (
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {Object.entries(message.parameters).map(([key, value]) => (
                      <Chip
                        key={key}
                        label={`${key}: ${value}`}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <ReadableTextRenderer text={message.text || '[Empty message]'} />
          )}

          {/* Summary Chip */}
          {message.summary && (
            <Chip
              icon={<AnalyticsIcon />}
              label={message.summary}
              variant="outlined"
              size="small"
              sx={{ mt: 1, mb: 1 }}
            />
          )}

          {/* Query Results Toggle */}
          {message.hasQuery && message.queryResults && (
            <QueryResultsSection queryResults={message.queryResults} />
          )}

          {/* Performance Indicators */}
          {message.sender === 'assistant' && message.performance && (
            <MessagePerformanceIndicator performance={message.performance} />
          )}

          {/* Expert & Cache Information */}
          {message.sender === 'assistant' && (message.expert || message.provider || message.cacheType || message.autoRouted) && (
            <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {message.expert && (
                <Chip
                  label={`${message.expert.name}`}
                  size="small"
                  sx={{ fontSize: '0.7em', height: '20px' }}
                  color="primary"
                  variant="outlined"
                />
              )}
              {message.provider && (
                <Chip
                  label={message.provider}
                  size="small"
                  sx={{ fontSize: '0.7em', height: '20px' }}
                  color="secondary"
                  variant="outlined"
                />
              )}
              {message.autoRouted && (
                <Chip
                  label={`⚙️ Auto: ${message.routingReason || 'Smart selection'}`}
                  size="small"
                  sx={{ fontSize: '0.7em', height: '20px' }}
                  color="primary"
                  variant="filled"
                  title={`Auto-routing selected ${message.provider} because: ${message.routingReason}`}
                />
              )}
              {message.cacheType && message.cacheType !== 'none' && (
                <Chip
                  label={`⚡ ${message.cacheType} cache`}
                  size="small"
                  sx={{ fontSize: '0.7em', height: '20px' }}
                  color="success"
                  variant="filled"
                />
              )}
              {message.responseTime && (
                <Chip
                  label={`${message.responseTime}ms`}
                  size="small"
                  sx={{ fontSize: '0.7em', height: '20px', opacity: 0.8 }}
                  variant="outlined"
                />
              )}
            </Box>
          )}

          <Typography variant="caption" sx={{ opacity: 0.6, mt: 1, display: 'block' }}>
            {message.timestamp.toLocaleTimeString()}
          </Typography>
        </Paper>
      </Box>
    );
  }, []); // End of useCallback - no dependencies as it doesn't use any changing state

  // Memoized Message Component to prevent unnecessary re-renders
  const MemoizedMessage = React.memo(({ message }) => {
    return renderMessage(message);
  });

  const ResearchSummary = ({ summary }) => {
    const [expanded, setExpanded] = useState(false);

    // Safety check - ensure summary is an array
    if (!summary || !Array.isArray(summary) || summary.length === 0) {
      return null;
    }

    return (
      <Box>
        <Button
          size="small"
          onClick={() => setExpanded(!expanded)}
          sx={{ color: 'inherit', p: 0 }}
        >
          {expanded ? 'Hide' : 'Show'} Research Steps
        </Button>
        <Collapse in={expanded}>
          <Stepper orientation="vertical" sx={{ mt: 1 }}>
            {summary.map((step, index) => (
              <Step key={index} active={true} completed={step.success}>
                <StepLabel
                  StepIconComponent={step.success ? CheckCircleIcon : ErrorIcon}
                  sx={{ '& .MuiStepLabel-label': { color: 'inherit' } }}
                >
                  {step.description}
                </StepLabel>
                <StepContent>
                  <Typography variant="caption">
                    {step.success ? `${step.rowCount} rows found` : `Error: ${step.error}`}
                  </Typography>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Collapse>
      </Box>
    );
  };

  // Memoized ReadableTextRenderer to prevent text reprocessing on every keystroke
  const ReadableTextRenderer = React.memo(({ text }) => {
    // Memoize the formatted text to avoid reprocessing
    const formattedLines = useMemo(() => {
      const formatText = (rawText) => {
        if (!rawText || rawText.length < 100) {
          return [rawText]; // Short text doesn't need formatting
        }

        // Break text into readable chunks at sentence boundaries
        return rawText
          .replace(/\. ([A-Z])/g, '.\n\n$1') // Break at ". [Capital letter]"
          .replace(/(\*\*[^\*]+\*\*:)/g, '\n\n$1') // Break before bold labels
          .replace(/(## [^\n]+)/g, '\n\n$1\n') // Add spacing around headers
          .split('\n')
          .map(line => line.trim())
          .filter(line => line.length > 0) // Remove empty lines
          .map(line => {
            // Bold formatting
            const formattedLine = line.replace(/\*\*([^\*]+)\*\*/g, '<strong>$1</strong>');
            return formattedLine;
          })
          .filter(line => line.length > 0); // Remove any remaining empty lines
      };

      return formatText(text);
    }, [text]); // Only recalculate when text prop changes

    return (
      <Box sx={{ '& p': { mb: 1.5, lineHeight: 1.7 }, '& strong': { fontWeight: 600, color: '#1976d2' } }}>
        {formattedLines.map((line, index) => (
          <Typography
            key={index}
            variant="body1"
            component="p"
            dangerouslySetInnerHTML={{ __html: sanitizeInlineHTML(line) }}
            sx={{ mb: 1.5 }}
          />
        ))}
      </Box>
    );
  }); // End of React.memo for ReadableTextRenderer

  // QueryResults Section Component
  const QueryResultsSection = React.memo(({ queryResults }) => {
    const [expanded, setExpanded] = useState(false);

    if (!queryResults) return null;

    // Handle different queryResults formats
    let resultsToShow = [];

    if (Array.isArray(queryResults)) {
      // Multi-query template format: [{ id: "revenue_metrics", name: "Revenue Overview", results: [...] }]
      resultsToShow = queryResults.filter(result => result && result.results && result.results.length > 0);
    } else if (queryResults.results) {
      // Single query result format
      resultsToShow = [{ name: 'Query Results', results: queryResults.results }];
    } else {
      // Direct results array
      resultsToShow = [{ name: 'Query Results', results: Array.isArray(queryResults) ? queryResults : [] }];
    }

    if (resultsToShow.length === 0) return null;

    return (
      <Card sx={{ mt: 2, border: '1px solid', borderColor: 'divider' }}>
        <CardActions sx={{ py: 1 }}>
          <Button
            size="small"
            startIcon={<AnalyticsIcon />}
            onClick={() => setExpanded(!expanded)}
            sx={{ fontSize: '0.8rem' }}
          >
            {expanded ? 'Hide' : 'Show'} Raw Data ({resultsToShow.length} dataset{resultsToShow.length > 1 ? 's' : ''})
          </Button>
        </CardActions>
        <Collapse in={expanded}>
          <CardContent sx={{ pt: 0 }}>
            {resultsToShow.map((resultSet, index) => (
              <Box key={index} sx={{ mb: index < resultsToShow.length - 1 ? 3 : 0 }}>
                {resultsToShow.length > 1 && (
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    {resultSet.name || `Dataset ${index + 1}`}
                  </Typography>
                )}
                <Box sx={{ maxHeight: '300px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <pre style={{
                    margin: 0,
                    padding: '12px',
                    fontSize: '11px',
                    lineHeight: '1.4',
                    backgroundColor: '#f8f9fa',
                    overflow: 'auto'
                  }}>
                    {JSON.stringify(resultSet.results, null, 2)}
                  </pre>
                </Box>
              </Box>
            ))}
          </CardContent>
        </Collapse>
      </Card>
    );
  });

  // Memoized HTMLRenderer to prevent flickering on every keystroke
  const HTMLRenderer = React.memo(({ html }) => {
    const [showHTML, setShowHTML] = useState(true);

    // Memoize the complete HTML to avoid regeneration on every render
    const completeHTML = useMemo(() => {
      if (html.includes('<!DOCTYPE html>')) return html;

      return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          /* Base styles with improved readability */
          body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif; 
            margin: 0; 
            padding: 15px; 
            line-height: 1.7; 
            width: 100%; 
            background-color: #fafafa;
            color: #333;
          }
          
          /* Table improvements */
          table {
            border-collapse: collapse;
            width: auto;
            margin: 25px 0; 
            box-shadow: 0 2px 4px rgba(0,0,0,0.05);
            border-radius: 6px;
            overflow: hidden;
          }
          th, td { 
            border: 1px solid #e0e0e0; 
            padding: 14px 16px; 
            text-align: left; 
          }
          th { 
            background-color: #f8f9fa; 
            font-weight: 600;
            color: #2c3e50;
            border-bottom: 2px solid #dee2e6;
          }
          
          /* Typography improvements */
          h1, h2, h3 { 
            color: #2c3e50; 
            margin-top: 35px; 
            margin-bottom: 15px;
            font-weight: 600;
          }
          h1 { font-size: 28px; }
          h2 { font-size: 24px; }
          h3 { font-size: 20px; }
          
          /* Paragraph spacing */
          p { 
            margin: 15px 0; 
            line-height: 1.7;
          }
          
          /* Enhanced metric styling */
          .metric { 
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            padding: 15px 18px; 
            border-radius: 8px; 
            margin: 15px 0;
            border-left: 4px solid #007bff;
            box-shadow: 0 2px 4px rgba(0,0,0,0.05);
          }
          
          /* MCP Response styling improvements */
          .mcp-response {
            background: white;
            border-radius: 12px;
            padding: 20px;
            margin: 15px 0;
            box-shadow: 0 4px 12px rgba(0,0,0,0.08);
            border: 1px solid #e9ecef;
          }
          
          .mcp-header {
            border-bottom: 2px solid #f8f9fa;
            padding-bottom: 15px;
            margin-bottom: 20px;
          }
          
          .mcp-header h3 {
            margin: 0 0 10px 0;
            color: #2c3e50;
            font-size: 22px;
            font-weight: 600;
          }
          
          .mcp-tools {
            margin-top: 12px;
          }
          
          .tools-label {
            font-weight: 500;
            color: #6c757d;
            margin-right: 8px;
          }
          
          .tool-badge {
            background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
            color: white;
            padding: 4px 12px;
            border-radius: 16px;
            font-size: 12px;
            font-weight: 500;
            margin-right: 8px;
            display: inline-block;
            box-shadow: 0 2px 4px rgba(0,123,255,0.2);
          }
          
          .response-text {
            font-size: 15px;
            line-height: 1.7;
            margin: 20px 0;
          }
          
          .response-text p {
            margin: 18px 0;
            text-align: justify;
            line-height: 1.8;
          }
          
          .response-text p:first-child {
            margin-top: 0;
          }
          
          .response-text p:last-child {
            margin-bottom: 0;
          }
          
          .response-text h3 {
            color: #2c3e50;
            font-size: 18px;
            font-weight: 600;
            margin: 25px 0 12px 0;
            border-bottom: 2px solid #007bff;
            padding-bottom: 5px;
          }
          
          .response-text h4 {
            color: #2c3e50;
            font-size: 16px;
            font-weight: 600;
            margin: 20px 0 10px 0;
            border-left: 4px solid #007bff;
            padding-left: 10px;
            background: rgba(0, 123, 255, 0.05);
            padding: 8px 0 8px 10px;
            border-radius: 0 4px 4px 0;
          }
          
          .response-text ul {
            margin: 15px 0;
            padding-left: 20px;
          }
          
          .response-text li {
            margin: 8px 0;
            line-height: 1.6;
          }
          
          .response-text strong {
            color: #2c3e50;
            font-weight: 600;
            background: rgba(0, 123, 255, 0.1);
            padding: 2px 4px;
            border-radius: 3px;
          }
          
          .response-text em {
            font-style: italic;
            color: #495057;
          }
          
          .mcp-meta {
            background: #f8f9fa;
            padding: 12px 16px;
            border-radius: 8px;
            margin-top: 20px;
            border-left: 4px solid #28a745;
          }
          
          .mcp-meta small {
            color: #6c757d;
            font-size: 13px;
            font-weight: 500;
          }
        </style>
      </head>
      <body>
        ${html}
      </body>
      </html>
    `;
    }, [html]); // Only recalculate when html prop changes

    return (
      <Box>
        <Box sx={{ mb: 2 }}>
          <Button
            startIcon={<VisibilityIcon />}
            onClick={() => setShowHTML(!showHTML)}
            size="small"
            variant="contained"
          >
            {showHTML ? 'Hide' : 'Show'} Enhanced Analysis Report
          </Button>
        </Box>

        <Collapse in={showHTML}>
          <Box
            sx={{
              border: '1px solid #ddd',
              borderRadius: 1,
              overflow: 'visible', // Changed from 'hidden' to allow full content
              width: '100%',
              minHeight: '700px'
            }}
          >
            <iframe
              srcDoc={completeHTML}
              // Model-written HTML: scripts may run (Chart.js charts) but never with the app's origin (DT-1482)
              sandbox="allow-scripts"
              style={{
                width: '100%',
                minHeight: '700px',
                height: 'auto',
                border: 'none',
                display: 'block'
              }}
              title="Analysis Report"
              scrolling="auto"
            />
          </Box>
        </Collapse>
      </Box>
    );
  }); // End of React.memo for HTMLRenderer

  const QueryResultsDisplay = ({ queryResults }) => {
    const [showQueries, setShowQueries] = useState(false);

    return (
      <Box sx={{ mt: 2 }}>
        <Button
          startIcon={<CodeIcon />}
          onClick={() => setShowQueries(!showQueries)}
          size="small"
          variant="outlined"
        >
          {showQueries ? 'Hide' : 'Show'} Research Details ({(queryResults || []).length} steps)
        </Button>

        <Collapse in={showQueries}>
          <Box sx={{ mt: 2 }}>
            {(queryResults || []).map((result, index) => (
              <Card key={index} sx={{ mb: 2, bgcolor: 'grey.50' }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Research Step {result.step}: {result.reasoning}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" paragraph>
                    {result.rowCount} rows returned
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.900', color: 'white', fontFamily: 'monospace' }}>
                    <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                      {result.sql}
                    </Typography>
                  </Paper>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Collapse>
      </Box>
    );
  };

  // Load available templates for the current brand and expert
  const loadAvailableTemplates = async () => {
    if (!selectedBrandId || !selectedExpertId) return;

    try {
      const response = await axios.get(`/api/chat/experts/${selectedExpertId}/quick-templates?limit=10`);
      if (response.data?.templates) {
        setAvailableTemplates(response.data.templates);
      }
    } catch (error) {
      console.error('Error loading available templates:', error);
    }
  };

  // Execute template with dynamic parameters
  const executeTemplateWithParameters = async (templateId, parameters) => {
    if (!selectedBrandId) {
      console.error('Please select a brand first');
      return;
    }

    setLoading(true);
    setShowTemplateDialog(false);

    // Check if this is a recipe
    const isRecipe = selectedTemplate?.template_type === 'recipe' || selectedTemplate?.is_recipe;

    if (isRecipe) {
      // Execute recipe with custom parameters
      // Note: executeRecipeDirectly handles its own loading state
      setLoading(false); // Reset loading since executeRecipeDirectly will manage it
      await executeRecipeDirectly({
        templateId: templateId,
        templateName: selectedTemplate?.name || templateId,
        parameters: parameters,
        isRecipe: true
      });
      return;
    }

    // Add template execution message
    const templateMessage = {
      id: Date.now(),
      type: 'template-execution',
      sender: 'user',
      text: `Executing template: ${selectedTemplate?.name || templateId}`,
      timestamp: new Date(),
      template: selectedTemplate,
      parameters: parameters
    };

    setMessages(prev => [...prev, templateMessage]);
    // Every write below appends to the current list (never a snapshot taken before the request) and only while the
    // chat is still this run's (beginChatRun, DT-1523)
    const { stillThisChat, addMessage: addToThisChat, end: endRun } = beginChatRun();

    // Check if this is a multi-query or playbook template - if so, use SSE streaming like main chat
    const isMultiQuery = selectedTemplate?.template_type === 'multi-query' || selectedTemplate?.template_type === 'multi_query';
    const isPlaybook = selectedTemplate?.template_type === 'playbook';

    if (isMultiQuery || isPlaybook) {
      console.log(`🌊 [Dynamic Template] ${isPlaybook ? 'Playbook' : 'Multi-query'} detected - using SSE streaming`);

      try {
        // Use same fetch approach as main chat - relative URL, no API_BASE_URL
        const controller = new AbortController();
        const timeoutMs = 300000; // 5 minutes
        const timeoutId = setTimeout(() => {
          console.error('⏱️ [Dynamic Template Timeout] Request exceeded', timeoutMs / 1000, 'seconds');
          controller.abort();
        }, timeoutMs);

        // Build headers with auth token (same as main chat)
        const fetchHeaders = { 'Content-Type': 'application/json' };
        try {
          const t = localStorage.getItem('token');
          if (t) {
            fetchHeaders['Authorization'] = `Bearer ${t}`;
            fetchHeaders['x-auth-token'] = t;
          }
        } catch (_) { }

        // Hint server we expect SSE
        fetchHeaders['Accept'] = 'text/event-stream';

        console.log('🚀 [Dynamic Template] Making SSE request');

        const response = await fetch('/api/chat/templates/execute', {
          method: 'POST',
          headers: fetchHeaders,
          body: JSON.stringify({
            templateId: templateId,
            brandId: parseInt(selectedBrandId),
            expertId: selectedExpertId,
            parameters: parameters,
            conversationId: conversationId
          }),
          signal: controller.signal,
          credentials: 'include'  // Match main chat exactly
        });

        clearTimeout(timeoutId);

        const ct = response.headers.get('content-type') || '';
        console.log('📡 [Dynamic Template] Response:', {
          contentType: ct,
          status: response.status,
          statusText: response.statusText,
          ok: response.ok
        });

        // ✅ CRITICAL: Check for HTTP errors BEFORE processing stream
        if (!response.ok) {
          let errorText = 'Unknown error';
          try {
            errorText = await response.text();
          } catch (e) {
            errorText = response.statusText;
          }
          console.error('❌ [Dynamic Template] HTTP Error:', response.status, errorText);
          // A refused conversation: drop the id and stop here, rather than re-sending it through the axios fallback
          // (DT-1514; the fallback's catch below handles the same code)
          let code = '';
          try { code = JSON.parse(errorText).code; } catch (parseErr) {
            console.debug('[Dynamic Template] error body is not JSON; treated as an ordinary error:', parseErr.message);
          }
          if (response.status === 403 && code === 'CONVERSATION_ACCESS_DENIED') {
            dropDeniedConversation(setConversationId, setMessages, conversationId, TEMPLATE_CONVERSATION_DENIED_TEXT, 'template stream');
            endRun();
            return;
          }
          throw new Error(`Server returned ${response.status}: ${errorText.substring(0, 200)}`);
        }

        const isStream = ct.includes('text/event-stream');

        if (isStream && response.body) {
          console.log('✅ [Dynamic Template] Processing SSE stream...');
          const reader = response.body.getReader();
          const decoder = new TextDecoder('utf-8');
          let buffer = '';

          try {
            while (true) {
              const { value, done } = await reader.read();
              if (done) {
                console.log('✅ [Dynamic Template] Stream completed');
                break;
              }

              buffer += decoder.decode(value, { stream: true });
              const events = buffer.split(/\r?\n\r?\n/);
              buffer = events.pop() || '';

              for (const eventText of events) {
                if (!eventText.trim()) continue;

                const lines = eventText.split(/\r?\n/);
                const dataLines = lines
                  .filter(l => l.startsWith('data:'))
                  .map(l => l.replace(/^data:\s?/, ''));

                if (dataLines.length === 0) continue;

                const payload = dataLines.join('\n');
                try {
                  const eventData = JSON.parse(payload);
                  console.log('📦 [Dynamic Template] SSE Event:', eventData.type);

                  if (eventData.type === 'PHASE_1_COMPLETE' && eventData.html) {
                    console.log('🔥 [Dynamic Template] Phase 1 - Adding query results');
                    addToThisChat({
                      id: Date.now(),
                      sender: 'assistant',
                      html: eventData.html,
                      timestamp: new Date(),
                      type: 'html'
                    });
                  } else if (eventData.type === 'PHASE_2_COMPLETE' && eventData.aiAnalysis) {
                    console.log('🔥 [Dynamic Template] Phase 2 - Adding AI analysis');
                    addToThisChat({
                      id: Date.now(),
                      sender: 'assistant',
                      html: eventData.aiAnalysis.content || '<div>AI analysis completed</div>',
                      timestamp: new Date(),
                      type: 'html'
                    });
                  } else if (eventData.type === 'STREAM_COMPLETE') {
                    console.log('🏁 [Dynamic Template] Agentic response received');
                    const responseText = eventData.response || eventData.aiResponse || '';
                    const responseHtml = eventData.html || null;
                    if (responseText || responseHtml) {
                      addToThisChat({
                        id: Date.now(),
                        sender: 'assistant',
                        text: responseText,
                        html: responseHtml,
                        timestamp: new Date(),
                        type: responseHtml ? 'html' : 'text'
                      });
                    }
                  } else if (eventData.type === 'ERROR') {
                    console.error('❌ [Dynamic Template] Error in stream:', eventData.error);
                    throw new Error(eventData.error);
                  }
                } catch (parseError) {
                  console.warn('⚠️ [Dynamic Template] SSE parse error:', parseError);
                }
              }
            }
          } finally {
            reader.releaseLock();
          }

          endRun();
          return;
        }
        // If not SSE, fall through to JSON/axios handling
      } catch (streamError) {
        console.error('❌ [Dynamic Template] Streaming failed:', streamError.message);
        // Fall through to axios fallback
      }
    }

    // A stream that failed after the user left this chat is not re-run: its answer would be dropped anyway
    if (!stillThisChat()) return;

    // Fallback: Use axios for single-query templates OR if streaming failed
    console.log('📝 [Dynamic Template] Using axios fallback');
    try {
      const response = await axios.post('/api/chat/templates/execute', {
        templateId: templateId,
        brandId: parseInt(selectedBrandId),
        expertId: selectedExpertId,
        parameters: parameters,
        conversationId: conversationId
      }, {
        timeout: 180000  // 3 minutes timeout for AI-powered template analysis
      });

      if (response.data?.success) {
        // 🔥 Capture message_id and conversation_id for debug logs
        if (response.data.message_id && stillThisChat()) {
          console.log('[Browse Template] ✅ Captured messageId:', response.data.message_id);
          setLastMessageId(response.data.message_id);
        }
        // Not adopted when we sent none: the server then answers with its message id as a stand-in, which is no stored
        // conversation, so /message would refuse it (DT-1249). The next typed message creates a real conversation.
        
        // Create dynamic template result message
        const resultMessage = {
          id: Date.now() + 1,
          type: 'dynamic-template-result',
          sender: 'assistant',
          text: `✅ Template executed successfully`,
          timestamp: new Date(),
          data: response.data.data || [],
          html: response.data.html || null, // Include HTML for multi-query templates
          template_type: response.data.template_type || 'single-query',
          template: response.data.template_info || selectedTemplate,
          parameters_used: response.data.parameters_used || parameters,
          dynamic_metadata: response.data.dynamic_metadata || {},
          execution_metadata: {
            ...response.data.execution_metadata,
            execution_time: response.data.execution_time || 0,
            template_id: templateId,
            expert_id: selectedExpertId,
            has_dynamic_grouping: true,
            message_id: response.data.message_id, // Include message_id in metadata
            conversation_id: response.data.conversation_id || conversationId // Include conversation_id
          }
        };

        addToThisChat(resultMessage);
      } else {
        throw new Error(response.data?.error || 'Template execution failed');
      }
    } catch (error) {
      console.error('Template execution error:', error);
      if (isConversationDenied(error)) {
        // same handling as the stream path above: the helper appends its warning to whatever is current and only while
        // the refused id is still the current one, so a late refusal never puts this thread back.
        dropDeniedConversation(setConversationId, setMessages, conversationId, TEMPLATE_CONVERSATION_DENIED_TEXT, 'template fallback');
      } else {
        addToThisChat({
          id: Date.now() + 1,
          type: 'error',
          sender: 'assistant',
          text: `❌ Template execution failed: ${error.message}`,
          timestamp: new Date()
        });
      }
    } finally {
      endRun();
    }
  };

  // Show template selection dialog
  const showTemplateSelector = (template) => {
    setSelectedTemplate(template);
    setShowTemplateDialog(true);
  };

  // Render template dialog
  const renderTemplateDialog = () => (
    <Dialog
      open={showTemplateDialog}
      onClose={() => setShowTemplateDialog(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TuneIcon />
          {selectedTemplate?.template_type === 'recipe' || selectedTemplate?.is_recipe ? 'Execute Recipe' : 'Execute Template'}: {selectedTemplate?.name}
          {selectedTemplate?.has_dynamic_params && (
            <Chip label="Dynamic" size="small" color="primary" />
          )}
          {(selectedTemplate?.template_type === 'recipe' || selectedTemplate?.is_recipe) && (
            <Chip label="Recipe" size="small" color="warning" />
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {selectedTemplate && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {selectedTemplate.description}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip label={`ID: ${selectedTemplate.id}`} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }} />
              <Chip label={selectedTemplate.template_type} size="small" color="primary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase' }} />
            </Box>
            <TemplateParameterControls
              template={selectedTemplate}
              onExecute={executeTemplateWithParameters}
              isLoading={loading}
              defaultExpanded={true}
              brandId={selectedBrandId}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowTemplateDialog(false)}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{ display: 'flex', flex: 1, height: 'calc(100vh - 64px)' }}>

      <Drawer
        variant="temporary"
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          }
        }}
      >
        <Divider />
        <Box sx={{ overflow: 'auto', p: 1 }}>
          <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', px: 2 }}>
            Conversations
          </Typography>

          {loadingConversations ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : conversations.length > 0 ? (
            <List>
              {conversations.map((conv) => (
                <ListItem key={conv.id} disablePadding>
                  <ListItemButton
                    selected={conv.id === conversationId}
                    onClick={() => loadConversation(conv.id)}
                    onContextMenu={(e) => handleContextMenu(e, conv)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: 'primary.light',
                        color: 'primary.contrastText',
                        '&:hover': {
                          backgroundColor: 'primary.main',
                        }
                      }
                    }}
                  >
                    <ListItemIcon>
                      <ChatIcon sx={{ color: conv.id === conversationId ? 'primary.contrastText' : 'inherit' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={conv.title || 'New Conversation'}
                      secondary={conv.updated_at ? new Date(conv.updated_at).toLocaleDateString() : ''}
                      primaryTypographyProps={{
                        noWrap: true,
                        variant: 'body2',
                        fontWeight: conv.id === conversationId ? 'bold' : 'normal'
                      }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        color: 'text.secondary'
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No conversations yet.
                Start a new conversation!
              </Typography>
            </Box>
          )}
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: 'grey.50',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        {/* Floating Action Buttons for Chat Controls */}
        <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1000, display: 'flex', gap: 1 }}>
          <Tooltip title="Conversation History">
            <IconButton
              onClick={() => setDrawerOpen(!drawerOpen)}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
                boxShadow: 2
              }}
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Header with Brand Selection */}
        <Paper sx={{ p: 1.5, borderRadius: 0, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <AnalyticsIcon sx={{ mr: 1 }} />
              AI Data Chat - Multi-Step Research Agent
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* MCP System Status (MCP-Only Mode) */}
              <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'primary.50', borderRadius: 2, px: 2, py: 1, border: '1px solid', borderColor: 'primary.200' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: mcpStatus?.available ? 'success.main' : 'error.main',
                    boxShadow: mcpStatus?.available ? '0 0 8px rgba(76, 175, 80, 0.6)' : 'none',
                    transition: 'box-shadow 0.3s ease'
                  }} />
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
                    MCP System
                  </Typography>
                  {loadingMcpStatus ? (
                    <Typography variant="caption" color="text.secondary">
                      Checking...
                    </Typography>
                  ) : mcpStatus ? (
                    <Typography
                      variant="caption"
                      sx={{
                        color: mcpStatus.available ? 'success.main' : 'error.main',
                        fontWeight: 500
                      }}
                    >
                      {mcpStatus.available ? 'Active' : 'Unavailable'}
                    </Typography>
                  ) : null}
                </Box>
              </Box>

              {/* Debug Logs Toggle - enabled when conversationId is set */}
              <Tooltip title={conversationId ? (showDebugLogs ? "Hide Debug Logs" : "Show Debug Logs") : "Send a message to view debug logs"}>
                <span>
                  <IconButton
                    size="small"
                    disabled={!conversationId}
                    onClick={() => {
                      if (conversationId) {
                        setShowDebugLogs(!showDebugLogs);
                      }
                    }}
                    sx={{
                      border: '2px solid',
                      borderColor: showDebugLogs ? 'primary.main' : 'grey.400',
                      bgcolor: showDebugLogs ? 'primary.light' : 'transparent',
                      '&:hover': {
                        bgcolor: showDebugLogs ? 'primary.main' : 'grey.100',
                      },
                      '&.Mui-disabled': {
                        borderColor: 'grey.300',
                        opacity: 0.5
                      }
                    }}
                  >
                    <BugReportIcon fontSize="small" color={showDebugLogs ? 'primary' : (conversationId ? 'action' : 'disabled')} />
                  </IconButton>
                </span>
              </Tooltip>

              {/* AI Model Selection */}
              <FormControl sx={{ minWidth: 150 }} size="small">
                <InputLabel>AI Model</InputLabel>
                <Select
                  value={aiProvider}
                  onChange={(e) => setAiProvider(e.target.value)}
                  label="AI Model"
                  size="small"
                >
                  <MenuItem value="auto">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography>Auto</Typography>
                      <Chip label="Smart" size="small" color="primary" sx={{ ml: 1, fontSize: '0.7em' }} />
                    </Box>
                  </MenuItem>
                  <MenuItem value="opus">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography>Opus</Typography>
                      <Chip label="Best" size="small" color="secondary" sx={{ ml: 1, fontSize: '0.7em' }} />
                    </Box>
                  </MenuItem>
                  <MenuItem value="claude-sonnet">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography>Claude Sonnet</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="gemini-pro">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography>Gemini Pro</Typography>
                      <Chip label="Cached" size="small" sx={{ ml: 1, fontSize: '0.7em' }} />
                    </Box>
                  </MenuItem>
                  <MenuItem value="gemini-flash">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography>Gemini Flash</Typography>
                      <Chip label="Fast" size="small" color="success" sx={{ ml: 1, fontSize: '0.7em' }} />
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Select Brand</InputLabel>
                <Select
                  value={selectedBrandId}
                  onChange={handleBrandChange}
                  disabled={loadingBrands}
                  size="small"
                  label="Select Brand"
                >
                  {(availableBrands || []).map((brand) => (
                    <MenuItem key={brand.id} value={brand.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Typography>{brand.name}</Typography>
                        {brand.hasData && (
                          <Chip
                            label={`${(brand.totalRows || 0).toLocaleString()} rows`}
                            size="small"
                            color="success"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {FEATURE_EXPERTS && selectedBrandId && (
                <>
                  <FormControl sx={{ minWidth: 220 }}>
                    <InputLabel>Expert</InputLabel>
                    <Select
                      value={selectedExpertId}
                      onChange={handleExpertChange}
                      disabled={expertsLoading || (experts || []).length === 0}
                      size="small"
                      label="Expert"
                    >
                      {(experts || []).map((ex) => (
                        <MenuItem key={ex.id} value={ex.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <Typography sx={{ fontWeight: 600, mr: 1 }}>{ex.name}</Typography>
                            <Chip label={ex.type} size="small" sx={{ textTransform: 'capitalize' }} />
                          </Box>
                        </MenuItem>
                      ))}
                      {(experts || []).length === 0 && (
                        <MenuItem value="" disabled>
                          {expertsLoading ? 'Loading experts...' : 'No experts yet'}
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </>
              )}
            </Box>
          </Box>

          {/* Brand Info and Template System Status removed as requested */}
        </Paper>

        {/* Dynamic Template Quick Actions - HIDDEN: Functionality moved to bottom row */}
        {/* {renderTemplateQuickActions()} */}

        {/* Conversation Debug Logs - Only render when conversationId exists */}
        {conversationId && (
          <ConversationDebugLogs
            conversationId={conversationId}
            companyId={1}
            messageId={lastMessageId}
            open={showDebugLogs}
            onClose={() => setShowDebugLogs(false)}
          />
        )}

        {/* Research Progress Indicator */}
        {researchProgress && (
          <ResearchProgressBar progress={researchProgress} />
        )}

        {/* Messages Area */}
        <Box sx={{
          flex: 1,
          overflow: 'auto',
          px: 1, // Only horizontal padding
          py: 0.5, // Minimal vertical padding
          background: 'linear-gradient(to bottom, #f5f5f5, #e3f2fd)',
          width: '100%',
          minHeight: 0 // Allow flex shrinking
        }}>
          {(messages || []).map((message) => (
            <div key={message.id}>
              <MessageRenderer message={message} />
            </div>
          ))}

          {restoringHistory && (
            <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">Restoring your conversation...</Typography>
            </Box>
          )}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
              <Paper sx={{
                p: 2,
                bgcolor: 'grey.100',
                borderRadius: '18px 18px 18px 4px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                <Typography variant="body2">
                  AI is conducting comprehensive multi-step research...
                </Typography>
              </Paper>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Input Area */}
        <Paper sx={{ p: 1.5, borderRadius: 0, flexShrink: 0 }}>
          {/* Hybrid UI: Slim Quick Access Bar + Expandable Panel */}
          {showQuickActions && selectedBrandId && (
            <Box sx={{ mb: 1.5 }}>
              {/* Slim Quick Access Bar - Always Visible */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.5,
                  px: 1,
                  backgroundColor: '#f8f9fa',
                  borderRadius: 1,
                  border: '1px solid #e0e0e0',
                  minHeight: '40px',
                  flexWrap: 'wrap'
                }}
              >
                {/* Chat Document Upload - on the left */}
                {selectedBrandId && (
                  <ChatDocumentUpload
                    conversationId={conversationId}
                    ensureConversationId={ensureUploadConversationId}
                    brandId={selectedBrandId}
                    expertId={selectedExpertId}
                    onDocumentProcessed={(docId, data, docConversationId) => {
                      console.log('Chat document processed:', docId, data);
                      // An upload that finishes after a reset belongs to the abandoned chat: its documents must not
                      // fill the new chat's list, which is sent with every message (DT-1562)
                      const current = conversationIdRef.current;
                      if ((docConversationId || current) !== current) return;
                      loadConversationDocuments(current);
                    }}
                    onDocumentDeleted={(docId) => {
                      console.log('Chat document deleted:', docId);
                      // The delete may answer after a reset: reload the conversation on screen now, never the one
                      // this render had (DT-1562 sweep)
                      loadConversationDocuments(conversationIdRef.current);
                    }}
                    onConversationDenied={(deniedId) => dropDeniedConversation(setConversationId, setMessages, deniedId, UPLOAD_CONVERSATION_DENIED_TEXT, 'upload')}
                    compact={true}
                  />
                )}

                {/* Pinned/Favourite Template Chips - show ALL pinned templates with gear buttons */}
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', flex: 1, alignItems: 'center' }}>
                  {quickAccessTemplates.length > 0 ? (
                    quickAccessTemplates.map((template, index) => (
                      <Box
                        key={template.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor: '#fff',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          px: 1.5,
                          py: 0.75,
                          gap: 1,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: '#f5f5f5',
                            borderColor: '#4caf50',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }
                        }}
                        onClick={() => {
                          // Parse default parameters from template
                          const defaultParams = {};
                          const paramSource = template.resolved_parameters || template.parameters;
                          if (paramSource) {
                            try {
                              const paramSchema = typeof paramSource === 'string' ? JSON.parse(paramSource) : paramSource;
                              for (const [key, param] of Object.entries(paramSchema)) {
                                if (param.default !== undefined) {
                                  defaultParams[key] = param.default;
                                } else if (param.type === 'date') {
                                  if (key.includes('start') || key.includes('from')) {
                                    defaultParams[key] = 'today-30';
                                  } else if (key.includes('end') || key.includes('to')) {
                                    defaultParams[key] = 'today';
                                  }
                                }
                              }
                            } catch (e) { /* ignore parse errors */ }
                          }
                          // Execute with proper template_execution format
                          handleQuickTemplateSelect({
                            type: 'template_execution',
                            templateId: template.id,
                            templateName: template.display_label || template.name,
                            parameters: defaultParams,
                            template_type: template.template_type, // Pass template_type for multi-query detection
                            isQuickReport: true
                          });
                        }}
                      >
                        {/* Template name */}
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: '#333',
                            fontSize: '0.85rem',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {template.display_label || template.name}
                        </Typography>

                        {/* Gear button with badge - click to open parameter dialog */}
                        <Tooltip title="Configure parameters">
                          <Badge
                            badgeContent={index + 1}
                            color="primary"
                            sx={{
                              '& .MuiBadge-badge': {
                                fontSize: '0.65rem',
                                height: '16px',
                                minWidth: '16px',
                                top: -2,
                                right: -2
                              }
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Get parameters - TemplateParameterControls expects a JSON STRING
                                const paramSource = template.resolved_parameters || template.parameters;
                                // Ensure parameters is a string for TemplateParameterControls
                                const paramsString = typeof paramSource === 'string'
                                  ? paramSource
                                  : JSON.stringify(paramSource);
                                // Set the template with string parameters for the dialog
                                setSelectedTemplate({
                                  ...template,
                                  parameters: paramsString,
                                  name: template.display_label || template.name
                                });
                                setShowTemplateDialog(true);
                              }}
                              disabled={loading}
                              sx={{
                                p: 0.5,
                                color: '#4caf50',
                                '&:hover': {
                                  backgroundColor: 'rgba(76, 175, 80, 0.1)'
                                }
                              }}
                            >
                              <TuneIcon sx={{ fontSize: '1.25rem' }} />
                            </IconButton>
                          </Badge>
                        </Tooltip>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No favourites pinned yet. Click Browse to add some!
                    </Typography>
                  )}
                </Box>

                {/* Browse/Expand Button */}
                <Button
                  size="small"
                  variant={templatePanelExpanded ? "contained" : "outlined"}
                  onClick={() => setTemplatePanelExpanded(!templatePanelExpanded)}
                  startIcon={templatePanelExpanded ? <TuneIcon /> : <SearchIcon />}
                  sx={{
                    textTransform: 'none',
                    minWidth: 'auto',
                    px: 1.5,
                    py: 0.5,
                    fontSize: '0.8rem'
                  }}
                >
                  {templatePanelExpanded ? 'Close' : 'Browse'}
                </Button>

                {/* New Conversation Button - inline after Browse */}
                <Tooltip title="Start New Conversation">
                  <IconButton
                    onClick={createNewConversation}
                    size="small"
                    sx={{
                      bgcolor: 'success.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'success.dark' },
                      width: 32,
                      height: 32
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

            </Box>
          )}

          {/* Right-side Drawer for Template Browser */}
          <Drawer
            anchor="right"
            open={templatePanelExpanded}
            onClose={() => setTemplatePanelExpanded(false)}
            sx={{
              '& .MuiDrawer-paper': {
                width: '480px',
                maxWidth: '90vw',
                boxSizing: 'border-box',
              }
            }}
          >
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* Drawer Header */}
              <Box sx={{
                p: 2,
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#fafafa'
              }}>
                <Typography variant="h6">Template Browser</Typography>
                <IconButton onClick={() => setTemplatePanelExpanded(false)} size="small">
                  <TuneIcon />
                </IconButton>
              </Box>

              {/* Tabs */}
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  minHeight: 'auto',
                  backgroundColor: '#fafafa'
                }}
              >
                <Tab
                  label="Browse All"
                  sx={{
                    textTransform: 'none',
                    minHeight: 'auto',
                    py: 1,
                    fontSize: '0.85rem'
                  }}
                />
                <Tab
                  label="Favourites"
                  sx={{
                    textTransform: 'none',
                    minHeight: 'auto',
                    py: 1,
                    fontSize: '0.85rem'
                  }}
                />
              </Tabs>

              {/* Tab Content - Scrollable */}
              <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
                {activeTab === 0 && (
                  <TemplateBrowser
                    brandId={selectedBrandId}
                    expertId={selectedExpertId}
                    onExecute={(template) => {
                      showTemplateSelector(template);
                      setTemplatePanelExpanded(false);
                    }}
                    disabled={loading}
                  />
                )}

                {activeTab === 1 && (
                  <PinnedTemplates
                    brandId={selectedBrandId}
                    expertId={selectedExpertId}
                    onExecute={(template) => {
                      showTemplateSelector(template);
                      setTemplatePanelExpanded(false);
                    }}
                    disabled={loading}
                  />
                )}
              </Box>
            </Box>
          </Drawer>

          {/* 🔥 PHASE 5: Conversation Context Indicator */}
          {conversationId && conversationContext && (
            <ConversationContextIndicator
              context={conversationContext}
              onClear={handleClearContext}
              loading={contextLoading}
            />
          )}

          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              multiline
              maxRows={3}
              value={input}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              inputRef={messageInputRef}
              placeholder={
                selectedBrandId
                  ? "Ask me anything about your data. I'll conduct thorough multi-step research before providing comprehensive insights..."
                  : "Please select a brand first"
              }
              disabled={loading || !selectedBrandId}
              variant="outlined"
              size="small"
            />

            {/* Send Button */}
            <Tooltip title="Send">
              <span>
                <IconButton
                  onClick={handleSendMessage}
                  disabled={loading || !input.trim() || !selectedBrandId}
                  color="primary"
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                    '&:disabled': { bgcolor: 'grey.300' }
                  }}
                >
                  <SendIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

        </Paper>
      </Box>

      {/* Dynamic Template Dialog */}
      {renderTemplateDialog()}

      {/* Context Menu for conversation management */}
      <Menu
        anchorEl={contextMenuAnchor}
        open={Boolean(contextMenuAnchor)}
        onClose={handleCloseContextMenu}
      >
        <MenuItem onClick={() => {
          const currentTitle = selectedConversationForMenu?.title || 'New Conversation';
          const newTitle = prompt('Enter new title:', currentTitle);
          if (newTitle && newTitle.trim() && newTitle.trim() !== currentTitle) {
            handleUpdateConversationTitle(newTitle.trim());
          }
        }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          Edit title
        </MenuItem>
        <MenuItem onClick={handleArchiveConversation}>
          <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
          Archive
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ChatInterface;
